import { formatDisplayDateTime } from "../../utils/dateUtils";
import React, { useEffect, useState, useRef } from "react";
import { AxiosError } from "axios";
import { Datum, icdNotCovered } from "../../interface/IcdNotCovered";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "../../components/ui/button";
import { IcdNotCoveredAxiosInstance } from "../../Network/IcdNotCoveredAxiosInstance";
import { Option } from "../../interface/Option";
import ListOfSpecialty from "../Shared/Specialty";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import DataTable2 from "shared/DataTable2";
import UnifiedSearchBox from "../../shared/UnifiedSearchBox";
import ServerDeletedPills from "shared/ServerDeletedPills";
import { ServerDeletedFilterValue } from "shared/ServerListFilters";
import { useServerPaginatedList } from "../../shared/useServerPaginatedList";
import ListOfNewNetworkName from "../Shared/NewNetworkName";
import Listofmedicalcode from "../Shared/ListofIcdmedicalcode";
import ListOfInsuranceplanName from "../Shared/ListOfInsuranceplanName";
import VipPatientSelector from "Pages/Shared/VipPatient";
import ListOfProviderName from "../Shared/ListOfProviderName";
import PolicyNames from "../Shared/PolicyNames";
import ListofMedicalGroup from "../Shared/ListofMedicalGroup";
import ListofCustomIcd from "../Shared/ListofcustemIcd";
import FileUpload from "Pages/uploadFile/UploadFileIcdNotCovered/UploadFileIcdNotCovered";
import ListOfDoctorName from "../Shared/Doctor";
import Vip from "../Shared/Vip";

export interface PolicyItem {
  id?: number;
  Policy_id: string;
  Policy_name: string;
  plans?: Array<{
    Plan_name?: string;
    plan_name?: string;
    name?: string;
    id?: number;
    [key: string]: any;
  }>;
}

// ─── Role Helper ──────────────────────────────────────────────────────────────
const getUserRole = (): string => {
  try {
    const token = localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType || "";
  } catch { return ""; }
};

// ─── preserveScroll helper ────────────────────────────────────────────────────
const preserveScrollCheckbox = (e: React.ChangeEvent<HTMLInputElement>, fn: () => void) => {
  const el = e.currentTarget as HTMLElement;
  const dialog = el.closest('[role="dialog"]');
  const scrollable = dialog?.querySelector('.overflow-y-auto') as HTMLElement | null;
  const scrollTop = scrollable?.scrollTop ?? 0;
  fn();
  requestAnimationFrame(() => {
    if (scrollable) scrollable.scrollTop = scrollTop;
  });
};

// ─── EntityCard ───────────────────────────────────────────────────────────────
const colors: Record<string, { border: string; bg: string; text: string; badgeBg: string; checkBg: string }> = {
  blue:    { border: "border-blue-200",    bg: "bg-blue-50",    text: "text-blue-700",    badgeBg: "bg-blue-50 text-blue-700 border-blue-200",    checkBg: "bg-blue-50/30 border-blue-100" },
  cyan:    { border: "border-cyan-200",    bg: "bg-cyan-50",    text: "text-cyan-700",    badgeBg: "bg-cyan-50 text-cyan-700 border-cyan-200",    checkBg: "bg-cyan-50/30 border-cyan-100" },
  purple:  { border: "border-purple-200",  bg: "bg-purple-50",  text: "text-purple-700",  badgeBg: "bg-purple-50 text-purple-700 border-purple-200",  checkBg: "bg-purple-50/30 border-purple-100" },
  amber:   { border: "border-amber-200",   bg: "bg-amber-50",   text: "text-amber-700",   badgeBg: "bg-amber-50 text-amber-700 border-amber-200",   checkBg: "bg-amber-50/30 border-amber-100" },
  emerald: { border: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-700", badgeBg: "bg-emerald-50 text-emerald-700 border-emerald-200", checkBg: "bg-emerald-50/30 border-emerald-100" },
  pink:    { border: "border-pink-200",    bg: "bg-pink-50",    text: "text-pink-700",    badgeBg: "bg-pink-50 text-pink-700 border-pink-200",    checkBg: "bg-pink-50/30 border-pink-100" },
  yellow:  { border: "border-yellow-200",  bg: "bg-yellow-50",  text: "text-yellow-700",  badgeBg: "bg-yellow-50 text-yellow-700 border-yellow-200",  checkBg: "bg-yellow-50/30 border-yellow-100" },
  indigo:  { border: "border-indigo-200",  bg: "bg-indigo-50",  text: "text-indigo-700",  badgeBg: "bg-indigo-50 text-indigo-700 border-indigo-200",  checkBg: "bg-indigo-50/30 border-indigo-100" },
  slate:   { border: "border-slate-200",   bg: "bg-slate-50",   text: "text-slate-700",   badgeBg: "bg-slate-50 text-slate-700 border-slate-200",   checkBg: "bg-slate-50/30 border-slate-100" },
  teal:    { border: "border-teal-200",    bg: "bg-teal-50",    text: "text-teal-700",    badgeBg: "bg-teal-50 text-teal-700 border-teal-200",    checkBg: "bg-teal-50/30 border-teal-100" },
};

// ─── QA Automation IDs ───────────────────────────────────────────────────────
// Stable selectors for Playwright / Cypress / Selenium.
// Additive only: no API, payload, validation logic, permissions, routing,
// visual style, element order, or UI behavior changes.
const QA_PAGE_PREFIX = "icd-not-covered";

const qaSafe = (value: string | number | undefined | null) =>
  String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const qaId = (suffix: string | number) => `${QA_PAGE_PREFIX}-${qaSafe(suffix)}`;

const qaAttrs = (suffix: string | number) => ({
  id: qaId(suffix),
  "data-testid": qaId(suffix),
  "data-qa": qaId(suffix),
});

const rowQaId = (action: string, id: string | number | undefined) =>
  qaId(`${action}-${id ?? "unknown"}`);

interface EntityCardProps {
  label: string;
  icon: string;
  color: string;
  isActive: boolean;
  mode: "EXCLUSION" | "INCLUSION" | null;
  onExclusion: () => void;
  onInclusion: () => void;
  onClear: () => void;
  automationId: string;
  children: React.ReactNode;
}

const EntityCard: React.FC<EntityCardProps> = ({
  label, icon, color, isActive, mode, onExclusion, onInclusion, onClear, automationId, children,
}) => {
  const c = colors[color] ?? colors.slate;

  const preserveScroll = (fn: () => void) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const dialog = (e.currentTarget as HTMLElement).closest('[role="dialog"]');
    const scrollable = dialog?.querySelector('.overflow-y-auto') as HTMLElement | null;
    const scrollTop = scrollable?.scrollTop ?? 0;
    fn();
    requestAnimationFrame(() => {
      if (scrollable) scrollable.scrollTop = scrollTop;
    });
  };

  return (
    <div
      id={automationId}
      data-testid={automationId}
      data-qa={automationId}
      data-qa-active={isActive ? "true" : "false"}
      data-qa-mode={mode ?? "DISABLED"}
      className={`bg-white rounded-xl border p-5 min-w-0 transition-all relative ${isActive ? `${c.border} shadow-sm` : "border-slate-100"}`}
    >
      {isActive && (
        <button
          type="button"
          id={`${automationId}-clear`}
          data-testid={`${automationId}-clear`}
          data-qa={`${automationId}-clear`}
          onClick={preserveScroll(onClear)}
          className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all border border-slate-100 shadow-sm z-10"
          title="Clear"
        >
          <i
            id={`${automationId}-clear-icon`}
            data-testid={`${automationId}-clear-icon`}
            data-qa={`${automationId}-clear-icon`}
            className="fa-solid fa-xmark text-[14px]"
          ></i>
        </button>
      )}
      <div className="flex flex-col items-center gap-4">
        <span
          id={`${automationId}-badge`}
          data-testid={`${automationId}-badge`}
          data-qa={`${automationId}-badge`}
          className={`px-4 py-1 rounded-full text-[10px] font-black tracking-tighter border flex items-center gap-2 ${isActive ? c.badgeBg : "bg-slate-50 text-slate-400 border-slate-200"}`}
        >
          <i
            id={`${automationId}-badge-icon`}
            data-testid={`${automationId}-badge-icon`}
            data-qa={`${automationId}-badge-icon`}
            className={`fa-solid ${icon} text-[12px]`}
          ></i>
          <span
            id={`${automationId}-badge-label`}
            data-testid={`${automationId}-badge-label`}
            data-qa={`${automationId}-badge-label`}
          >
            {label}
          </span>
        </span>
        <div
          id={`${automationId}-mode-selector`}
          data-testid={`${automationId}-mode-selector`}
          data-qa={`${automationId}-mode-selector`}
          className="flex bg-slate-100/80 rounded-lg p-1 border border-slate-200 w-full shadow-inner"
        >
          <button
            type="button"
            id={`${automationId}-exclusion`}
            data-testid={`${automationId}-exclusion`}
            data-qa={`${automationId}-exclusion`}
            data-qa-action="select-exclusion"
            data-qa-selected={isActive && mode === "EXCLUSION" ? "true" : "false"}
            onClick={preserveScroll(onExclusion)}
            className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isActive && mode === "EXCLUSION" ? "bg-red-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
          >
            Exclusion
          </button>
          <button
            type="button"
            id={`${automationId}-inclusion`}
            data-testid={`${automationId}-inclusion`}
            data-qa={`${automationId}-inclusion`}
            data-qa-action="select-inclusion"
            data-qa-selected={isActive && mode === "INCLUSION" ? "true" : "false"}
            onClick={preserveScroll(onInclusion)}
            className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isActive && mode === "INCLUSION" ? "bg-emerald-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
          >
            Inclusion
          </button>
        </div>
      </div>
      {isActive && (
        <div
          id={`${automationId}-content`}
          data-testid={`${automationId}-content`}
          data-qa={`${automationId}-content`}
          className="mt-5 pt-5 border-t border-slate-100 space-y-4 min-w-0"
        >
          {children}
        </div>
      )}
    </div>
  );
};
// ─── SectionCard ──────────────────────────────────────────────────────────────
const SectionCard: React.FC<{ num: number; title: string; subtitle?: string; automationId?: string; children: React.ReactNode }> = ({ num, title, subtitle, automationId, children }) => (
  <div {...(automationId ? { id: automationId, "data-testid": automationId, "data-qa": automationId } : {})} className="border border-gray-200 rounded-xl overflow-hidden mb-4 bg-white shadow-sm">
    <div
      {...(automationId ? { id: `${automationId}-header`, "data-testid": `${automationId}-header`, "data-qa": `${automationId}-header` } : {})}
      className="flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200"
    >
      <span
        {...(automationId ? { id: `${automationId}-number`, "data-testid": `${automationId}-number`, "data-qa": `${automationId}-number` } : {})}
        className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0"
      >
        {num}
      </span>
      <div>
        <p
          {...(automationId ? { id: `${automationId}-title`, "data-testid": `${automationId}-title`, "data-qa": `${automationId}-title` } : {})}
          className="text-sm font-semibold text-gray-800"
        >
          {title}
        </p>
        {subtitle && (
          <p
            {...(automationId ? { id: `${automationId}-subtitle`, "data-testid": `${automationId}-subtitle`, "data-qa": `${automationId}-subtitle` } : {})}
            className="text-xs text-gray-500"
          >
            {subtitle}
          </p>
        )}
      </div>
    </div>
    <div
      {...(automationId ? { id: `${automationId}-body`, "data-testid": `${automationId}-body`, "data-qa": `${automationId}-body` } : {})}
      className="p-5"
    >
      {children}
    </div>
  </div>
);

// ─── StatusToggle ─────────────────────────────────────────────────────────────
const StatusToggle: React.FC<{ label: string; dotColor: string; value: boolean; onChange: (v: boolean) => void; automationId?: string }> = ({ label, dotColor, value, onChange, automationId }) => (
  <div
    {...(automationId ? { id: automationId, "data-testid": automationId, "data-qa": automationId } : {})}
    data-qa-value={value ? "active" : "inactive"}
    className="flex gap-2"
  >
    <button
      {...(automationId ? { id: `${automationId}-active`, "data-testid": `${automationId}-active`, "data-qa": `${automationId}-active` } : {})}
      data-qa-selected={value ? "true" : "false"}
      type="button" onClick={() => onChange(true)}
      className={`flex-1 h-10 rounded-md text-sm font-medium border transition-all ${value ? "bg-green-50 text-green-800 border-green-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}
    >
      Active
    </button>
    <button
      {...(automationId ? { id: `${automationId}-inactive`, "data-testid": `${automationId}-inactive`, "data-qa": `${automationId}-inactive` } : {})}
      data-qa-selected={!value ? "true" : "false"}
      type="button" onClick={() => onChange(false)}
      className={`flex-1 h-10 rounded-md text-sm font-medium border transition-all ${!value ? "bg-red-50 text-red-800 border-red-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}
    >
      Inactive
    </button>
  </div>
);
// ─── mode conversion helpers ──────────────────────────────────────────────────
const modeToCard = (mode: string | undefined): { cardMode: "EXCLUSION" | "INCLUSION" | null; isActive: boolean } => {
  if (mode === "EXCLUDED") return { cardMode: "EXCLUSION", isActive: true };
  if (mode === "INCLUDED") return { cardMode: "INCLUSION", isActive: true };
  return { cardMode: null, isActive: false };
};

const cardToMode = (cardMode: "EXCLUSION" | "INCLUSION" | null): string => {
  if (cardMode === "EXCLUSION") return "EXCLUDED";
  if (cardMode === "INCLUSION") return "INCLUDED";
  return "DISABLED";
};

// ─── Main Component ───────────────────────────────────────────────────────────
// Keep the source status and SQL ID for already linked entities.
// Absence of a status field does not mean inactive.
const linkedIsInactive = (entry: any): boolean => {
  if (!entry || typeof entry !== "object") return false;
  const item = entry.value && typeof entry.value === "object" ? entry.value : entry;
  const disabled = (v: any) => v === false || v === 0 || v === "0" ||
    (typeof v === "string" && ["false", "inactive", "disabled"].includes(v.trim().toLowerCase()));
  const truthy = (v: any) => v === true || v === 1 || v === "1" || String(v).toLowerCase() === "true";
  return entry.__inactive === true || item.__inactive === true ||
    truthy(item.is_deleted ?? item.Is_deleted) ||
    disabled(item.is_active ?? item.Is_active ?? item.isActive ?? item.active ?? item.status ?? item.Status);
};

const linkedStatusFields = (entry: any) => ({
  __inactive: linkedIsInactive(entry),
  is_active: entry?.is_active ?? entry?.Is_active ?? entry?.isActive ?? entry?.active,
  status: entry?.status ?? entry?.Status,
  is_deleted: entry?.is_deleted ?? entry?.Is_deleted,
});

const viewStatusLabels = (source: any[], names: string[]): string[] =>
  names.map((name, index) => linkedIsInactive(source[index]) ? `${name} (Inactive)` : name);

const ICDNotCovered: React.FC = () => {
  const [items, setItems] = useState<icdNotCovered>({ data: [] });

  // ─── Role-based access ────────────────────────────────────────────────────
  const userRole = getUserRole();
  const canManageDeleted = userRole === "system_admin" || userRole === "super_super_admin";

  // ─── Force Dialog state ───────────────────────────────────────────────────
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceAction, setForceAction] = useState<"softDelete" | "hardDelete" | "status" | null>(null);
  const [forceId, setForceId] = useState<string | number | null>(null);
  const [forceError, setForceError] = useState<string>("");

  const [relationName, setRelationName] = useState<string>("");
  const [medicalcode, setMedicalcode] = useState<Option[]>([]);
  const [Description, setDescription] = useState<string>("");
  const [customIcdGroups, setCustomIcdGroups] = useState<Option[]>([]);
  const [medicalIcdGroups, setMedicalIcdGroups] = useState<Option[]>([]);

  const [provider, setProvider] = useState<Option[]>([]);
  const [providerMode, setProviderMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isProviderActive, setIsProviderActive] = useState(false);
  const [selectAllProviders, setSelectAllProviders] = useState(false);

  const [networks, setNetworks] = useState<Option[]>([]);
  const [networkMode, setNetworkMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isNetworkActive, setIsNetworkActive] = useState(false);
  const [selectAllNetworks, setSelectAllNetworks] = useState(false);

  const [insuranceplan, setInsuranceplan] = useState<Option[]>([]);
  const [insuranceMode, setInsuranceMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isInsuranceActive, setIsInsuranceActive] = useState(false);
  const [selectAllInsurance, setSelectAllInsurance] = useState(false);

  const [Payer, setPayer] = useState<string[]>([]);
  const [payerMode, setPayerMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPayerActive, setIsPayerActive] = useState(false);
  const [selectAllPayer, setSelectAllPayer] = useState(false);

  const [policy, setPolicy] = useState<PolicyItem[]>([]);
  const [policyMode, setPolicyMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPolicyActive, setIsPolicyActive] = useState(false);
  const [selectAllPolicy, setSelectAllPolicy] = useState(false);

  const [patients, setPatients] = useState<Option[]>([]);
  const [patientMode, setPatientMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPatientActive, setIsPatientActive] = useState(false);
  const [selectAllPatients, setSelectAllPatients] = useState(false);

  const [vip, setVip] = useState<Option[]>([]);
  const [vipMode, setVipMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isVipActive, setIsVipActive] = useState(false);
  const [selectAllVip, setSelectAllVip] = useState(false);

  const [doctor, setDoctor] = useState<Option[]>([]);
  const [doctorMode, setDoctorMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isDoctorActive, setIsDoctorActive] = useState(false);
  const [selectAllDoctor, setSelectAllDoctor] = useState(false);

  const [specialty, setSpecialty] = useState<Option[]>([]);
  const [specialtyMode, setSpecialtyMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isSpecialtyActive, setIsSpecialtyActive] = useState(false);
  const [selectAllSpecialty, setSelectAllSpecialty] = useState(false);
  const [clearSpecialty, setClearSpecialty] = useState(false);

  const [userLevel, setUserLevel] = useState<string[]>([]);
  const [userMode, setUserMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isUserActive, setIsUserActive] = useState(false);
  const [selectAllUser, setSelectAllUser] = useState(false);

  const [showEntities, setShowEntities] = useState(false);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [icdCodesDialogOpen, setIcdCodesDialogOpen] = useState(false);
  const [icdCodesDialogList, setIcdCodesDialogList] = useState<Array<{ label: string; kind: "icd" | "medical" | "custom" }>>([]);
  const [icdCodesDialogTitle, setIcdCodesDialogTitle] = useState("");
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
const [showAllUpdates, setShowAllUpdates] = useState(false);
  const [clearNetworks, setClearNetworks] = useState(false);
  const [clearmedicalcode, setClearMedicalcode] = useState(false);
  const [clearInsuranceplan, setClearInsuranceplan] = useState(false);
  const [clearCustomIcdGroups, setClearCustomIcdGroups] = useState(false);
  const [clearMedicalIcdGroups, setClearMedicalIcdGroups] = useState(false);
  const [clearProvider, setClearProvider] = useState(false);
  const [clearPatient, setClearPatient] = useState(false);
  const [clearPolicy, setClearPolicy] = useState(false);
  const [clearDoctor, setClearDoctor] = useState(false);
  const [clearVip, setClearVip] = useState(false);

  const [selectedMainOption, setSelectedMainOption] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [deletedFilter, setDeletedFilter] = useState<ServerDeletedFilterValue>("false");
  const [filterSearch, setFilterSearch] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<Datum | null>(null);

  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const token = localStorage.getItem("token") || "";
  const { toast } = useToast();
  const [usersMap, setUsersMap] = useState<Record<number, string>>({});

  // ─── QA Automation: nested shared dropdowns/search/file upload ──────────────
  // Some controls are rendered inside shared components / react-select.
  // This attaches stable selectors to the actual inner input/select element.
  const applyNestedControlQaAttrs = () => {
    const setControlQaAttrs = (wrapperQaId: string, controlTestId: string) => {
      const wrapper = document.getElementById(wrapperQaId);
      const control = wrapper?.querySelector("input, select, textarea") as
        | HTMLInputElement
        | HTMLSelectElement
        | HTMLTextAreaElement
        | null;

      if (control) {
        control.setAttribute("id", controlTestId);
        control.setAttribute("data-testid", controlTestId);
        control.setAttribute("data-qa", controlTestId);
      }
    };

    // Toolbar / Search
    setControlQaAttrs(qaId("wrapper-toolbar-search"), "icd-not-covered-input-search");

    // ICD source dropdowns
    setControlQaAttrs(qaId("wrapper-list-of-icd"), "icd-not-covered-select-icd");
    setControlQaAttrs(qaId("wrapper-medical-icd-groups"), "icd-not-covered-select-medical-icd-group");
    setControlQaAttrs(qaId("wrapper-custom-icd-groups"), "icd-not-covered-select-custom-icd-group");

    // Entity dropdowns
    setControlQaAttrs(qaId("wrapper-provider-selector"), "icd-not-covered-select-provider");
    setControlQaAttrs(qaId("wrapper-doctor-selector"), "icd-not-covered-select-doctor");
    setControlQaAttrs(qaId("wrapper-specialty-selector"), "icd-not-covered-select-specialty");
    setControlQaAttrs(qaId("wrapper-network-selector"), "icd-not-covered-select-network");
    setControlQaAttrs(qaId("wrapper-insurance-plan-selector"), "icd-not-covered-select-insurance-plan");
    setControlQaAttrs(qaId("wrapper-policy-selector"), "icd-not-covered-select-policy");
    setControlQaAttrs(qaId("wrapper-patient-selector"), "icd-not-covered-select-patient");
    setControlQaAttrs(qaId("wrapper-vip-selector"), "icd-not-covered-select-vip");
  };

  useEffect(() => {
    const timer = window.setTimeout(applyNestedControlQaAttrs, 0);
    return () => window.clearTimeout(timer);
  }, [
    isDialogOpen,
    selectedMainOption,
    showEntities,
    filterSearch,
    clearNetworks,
    clearmedicalcode,
    clearInsuranceplan,
    clearCustomIcdGroups,
    clearMedicalIcdGroups,
    clearProvider,
    clearPatient,
    clearPolicy,
    clearDoctor,
    clearVip,
    clearSpecialty,
  ]);


  const toDisplayString = (val: any): string => {
    if (val === null || val === undefined) return "";
    if (typeof val === "string" || typeof val === "number") return String(val);
    if (typeof val === "object") return val.name ?? val.label ?? val.value ?? val.at ?? val.id ?? JSON.stringify(val);
    return String(val);
  };


  const resetAllEntityStates = () => {
    setProvider([]);      setProviderMode(null);  setIsProviderActive(false);  setSelectAllProviders(false);
    setNetworks([]);      setNetworkMode(null);   setIsNetworkActive(false);   setSelectAllNetworks(false);
    setInsuranceplan([]); setInsuranceMode(null); setIsInsuranceActive(false); setSelectAllInsurance(false);
    setPayer([]);         setPayerMode(null);     setIsPayerActive(false);     setSelectAllPayer(false);
    setPolicy([]);        setPolicyMode(null);    setIsPolicyActive(false);    setSelectAllPolicy(false);
    setPatients([]);      setPatientMode(null);   setIsPatientActive(false);   setSelectAllPatients(false);
    setVip([]);           setVipMode(null);       setIsVipActive(false);       setSelectAllVip(false);
    setDoctor([]);        setDoctorMode(null);    setIsDoctorActive(false);    setSelectAllDoctor(false);
    setUserLevel([]);     setUserMode(null);      setIsUserActive(false);      setSelectAllUser(false);
    setSpecialty([]);     setSpecialtyMode(null); setIsSpecialtyActive(false); setSelectAllSpecialty(false);
    setClearProvider(true);
    setClearNetworks(true);
    setClearInsuranceplan(true);
    setClearPatient(true);
    setClearPolicy(true);
    setClearDoctor(true);
    setClearVip(true);
    setClearSpecialty(true);
  };

  const clear = () => {
    setRelationName("");
    setMedicalcode([]);
    setCustomIcdGroups([]);
    setMedicalIcdGroups([]);
    setSelectedMainOption(null);
    setIsActive(true);
    setShowEntities(false);
    resetAllEntityStates();
  };

  const handleToggleEntities = () => {
    if (showEntities) resetAllEntityStates();
    setShowEntities((prev) => !prev);
  };

  const validateEntities = (): string | null => {
    const checks = [
      { isActive: isProviderActive,   hasData: selectAllProviders  || provider.length > 0,     label: "Providers" },
      { isActive: isDoctorActive,     hasData: selectAllDoctor     || doctor.length > 0,        label: "Doctor" },
      { isActive: isNetworkActive,    hasData: selectAllNetworks   || networks.length > 0,      label: "Networks" },
      { isActive: isInsuranceActive,  hasData: selectAllInsurance  || insuranceplan.length > 0, label: "Insurance Plan" },
      { isActive: isPolicyActive,     hasData: selectAllPolicy     || policy.length > 0,        label: "Policy" },
      { isActive: isPatientActive,    hasData: selectAllPatients   || patients.length > 0,      label: "Patients" },
      { isActive: isVipActive,        hasData: selectAllVip        || vip.length > 0,           label: "VIP" },
      { isActive: isPayerActive,      hasData: selectAllPayer      || Payer.length > 0,         label: "Payer" },
      { isActive: isUserActive,       hasData: selectAllUser       || userLevel.length > 0,     label: "User Level" },
      { isActive: isSpecialtyActive,  hasData: selectAllSpecialty  || specialty.length > 0,     label: "Specialty" },
    ];
    const empty = checks.filter(c => c.isActive && !c.hasData).map(c => c.label);
    if (empty.length >= 1) {
      return `The following cards are open but have no data selected: ${empty.join(", ")}. Please select at least one item for each, or close them using the ✕ button.`;
    }
    return null;
  };

  const extractIcdNotCoveredDetail = (payload: any): any | null => {
    const candidates = [
      payload?.getIcdNotCovereds,
      payload?.getIcdNotCovered,
      payload?.result,
      payload?.results,
      payload?.data,
      payload,
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        const firstRecord = candidate.find(
          (entry: any) => entry && typeof entry === "object" && entry.id != null,
        );
        if (firstRecord) return firstRecord;
      } else if (candidate && typeof candidate === "object" && candidate.id != null) {
        return candidate;
      }
    }

    return null;
  };

  const fetchIcdNotCoveredById = async (
    id: string | number,
    fallbackItem?: Datum,
  ): Promise<Datum> => {
    try {
      const response = await IcdNotCoveredAxiosInstance.get(`getIcdNotCovered/${id}`);

      if (response.data?.success === false) {
        throw new Error(response.data?.error || "ICD Not Covered record not found.");
      }

      const apiDetail = extractIcdNotCoveredDetail(response.data);
      if (apiDetail) {
        return { ...(fallbackItem || {}), ...apiDetail } as Datum;
      }

      if (fallbackItem) return fallbackItem;
      throw new Error("ICD Not Covered record not found.");
    } catch (error) {
      if (fallbackItem) return fallbackItem;
      throw error;
    }
  };

  const handleOpenDialog = async (item: Datum) => {
    const detail = await fetchIcdNotCoveredById(item.id, item);
    setSelectedItem(detail);
    setTimeout(() => { dialogRef.current?.showModal(); }, 0);
  };

  const handleCloseDialog = () => { setSelectedItem(null); setShowAllUpdates(false); };
  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return "—";
    return formatDisplayDateTime(dateStr);
  };

  const handleEditClick = async (item: Datum) => {
    // Keep existing selections visible when opening Edit.
    // These clear flags may still be true from a previous Add/Clear operation.
    setClearProvider(false);
    setClearNetworks(false);
    setClearInsuranceplan(false);
    setClearPatient(false);
    setClearPolicy(false);
    setClearDoctor(false);
    setClearVip(false);
    setClearSpecialty(false);
    setClearMedicalcode(false);
    setClearCustomIcdGroups(false);
    setClearMedicalIcdGroups(false);

    const detail = await fetchIcdNotCoveredById(item.id, item);
    handleEdit(detail);
    setIsDialogOpen(true);
  };

  const handleAddClick = () => { setEditIndex(null); clear(); setIsDialogOpen(true); };

  useEffect(() => { fetchUsers(); }, []);

  const handleInsuranceplanChange = (selectedOptions: Option[]) => setInsuranceplan(selectedOptions);
  const handleNetworkChange = (selectedOptions: Option[]) => setNetworks(selectedOptions);
  const handlemedicalcodeChange = (selectedOptions: Option[]) => setMedicalcode(selectedOptions);
  const handleCustomIcdGroupsChange = (selectedOptions: Option[]) => setCustomIcdGroups(selectedOptions);
  const handleVipChange = (selectedOptions: Option[]) => setVip(selectedOptions);
  const handleMedicalIcdGroupsChange = (selectedOptions: Option[]) => setMedicalIcdGroups(selectedOptions);
  const handlePoliciesChange = (selectedOptions: PolicyItem[]) => setPolicy(selectedOptions);
  const handlePatientChange = (selectedOptions: Option[]) => setPatients(selectedOptions);
  const handleProviderChange = (selectedOptions: Option[]) => setProvider(selectedOptions);
  const handleDoctorChange = (selectedOptions: Option[]) => setDoctor(selectedOptions);


  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    allowedPageSizes,
    isTableLoading,
    manualPagination,
    fetchItems,
  } = useServerPaginatedList<Datum, { search: string; status: string; deleted: ServerDeletedFilterValue }>({
    filters: { search: filterSearch, status: filterStatus, deleted: deletedFilter },
    fetchPage: ({ page, limit, filters }) =>
      IcdNotCoveredAxiosInstance.get(
        "getIcdNotCovered",
        {
          params: {
            page,
            limit,
            name: filters.search.trim() || undefined,
            disease: filters.search.trim() || undefined,
            relation_name: filters.search.trim() || undefined,
            is_active: filters.status === "true" || filters.status === "false" ? filters.status : undefined,
            is_deleted: filters.deleted,
          },
        }
      ),
    setRows: (rows) => setItems({ data: rows }),
    sortRows: (rows) =>
      [...rows]
        .sort((a: any, b: any) => Number(b.id) - Number(a.id))
        .sort((a: any, b: any) =>
          a.is_deleted === b.is_deleted ? 0 : a.is_deleted ? 1 : -1
        ),
    onError: (error) => {
      console.error("Error fetching ICD Not Covered.", error);
      toast({
        title: "Error",
        description: "Error fetching ICD Not Covered.",
        variant: "destructive",
      });
    },
  });

  const fetchUsers = async () => {
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
      const map: Record<number, string> = {};
      raw.forEach((u: any) => { if (u.id != null) map[Number(u.id)] = u.name ?? u.username ?? String(u.id); });
      setUsersMap(map);
    } catch {}
  };

  const resolveUserName = (val: any): string => {
    if (val == null) return "—";
    if (typeof val === "object") return val.name ?? val.username ?? "—";
    if (typeof val === "string" && isNaN(Number(val))) return val;
    return usersMap[Number(val)] ?? String(val);
  };

  // ─── Soft Delete ──────────────────────────────────────────────────────────
  const removeItem = async (id: string | number): Promise<void> => {
    try {
      const response = await IcdNotCoveredAxiosInstance.delete(`softDeleteIcdNotCovered/${id}`, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "ICD Not Covered Deleted Successfully." });
      } else {
        setForceId(id);
        setForceAction("softDelete");
        setForceError(response.data.error || "This record is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "Failed to delete";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used")) {
        setForceId(id); setForceAction("softDelete"); setForceError(errMsg); setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg, variant: "destructive" });
      }
    }
  };

  // ─── Restore ──────────────────────────────────────────────────────────────
  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      const response = await IcdNotCoveredAxiosInstance.patch(`restoreIcdNotCovered/${id}`, { token });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "ICD Not Covered restored successfully." });
      } else {
        toast({ title: "Error", description: response.data.error || "Failed to restore", variant: "destructive" });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "Failed to restore", variant: "destructive" });
    }
  };

  // ─── Hard Delete (permanent) ──────────────────────────────────────────────
  const permanentDeleteItem = async (id: string | number, force = false): Promise<void> => {
    try {
      const url = force ? `deleteIcdNotCovered/${id}?force=true` : `deleteIcdNotCovered/${id}`;
      const response = await IcdNotCoveredAxiosInstance.delete(url, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "ICD Not Covered Permanently Deleted." });
      } else {
        setForceAction("hardDelete");
        setForceError(response.data.error || "This record is used elsewhere.");
      }
    } catch (error: any) {
      setForceAction("hardDelete");
      setForceError(error.response?.data?.error || "Failed to delete");
    }
  };

  // ─── Force Soft Delete ────────────────────────────────────────────────────
  const forceSoftDelete = async (id: string | number): Promise<void> => {
    try {
      const response = await IcdNotCoveredAxiosInstance.delete(`softDeleteIcdNotCovered/${id}?force=true`, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "ICD Not Covered force Deleted Successfully." });
      } else {
        setForceError(response.data.error || "Failed to force delete");
      }
    } catch (error: any) {
      setForceError(error.response?.data?.error || "Failed to force delete");
    }
  };

  const toggleActiveStatus = async (item: Datum) => {
    try {
      const ValuesProvider = toIdStringArray((item.provider || []) as any[]);
      const policyData = toIdStringArray((item.policy || []) as any[]);
      const updatedItem = {
        data: {
          relation_name: item.relation_name,
          icd: item.icd || [],
          custom_groups: (item.custom_groups || []).map((g: any) => g.id ?? g),
          medical_groups: (item.medical_groups || []).map((g: any) => g.id ?? g),
          provider: ValuesProvider,
          provider_mode: item.provider_mode ?? "DISABLED",
          provider_all: item.provider_all ?? false,
          network: toIdStringArray((item.network || []) as any[]),
          network_mode: item.network_mode ?? "DISABLED",
          network_all: item.network_all ?? false,
          insuranceplan: toIdStringArray((item.insuranceplan || []) as any[]),
          insuranceplan_mode: item.insuranceplan_mode ?? "DISABLED",
          insuranceplan_all: item.insuranceplan_all ?? false,
          payer: item.payer || [],
          payer_mode: item.payer_mode ?? "DISABLED",
          payer_all: item.payer_all ?? false,
          patient: toIdStringArray((item.patient || []) as any[]),
          patient_mode: item.patient_mode ?? "DISABLED",
          patient_all: item.patient_all ?? false,
          policy: policyData,
          policy_mode: item.policy_mode ?? "DISABLED",
          policy_all: item.policy_all ?? false,
          user_level: item.user_level || [],
          user_mode: item.user_mode ?? "DISABLED",
          user_all: item.user_all ?? false,
          vip: toIdStringArray((item.vip || []) as any[]),
          vip_mode: item.vip_mode ?? "DISABLED",
          vip_all: item.vip_all ?? false,
          doctor: toIdStringArray((item.doctor || []) as any[]),
          doctor_mode: item.doctor_mode ?? "DISABLED",
          doctor_all: item.doctor_all ?? false,
          specialty: toIdStringArray((item.specialty || []) as any[]),
          specialty_mode: item.specialty_mode ?? "DISABLED",
          specialty_all: item.specialty_all ?? false,
          is_active: !item.is_active,
          id: item.id,
        },
        token,
      };
      const response = await IcdNotCoveredAxiosInstance.put(`updateIcdNotCovered`, updatedItem);
      if (response.data.success) {
        setItems((prev) => ({ data: prev.data.map((r) => r.id === item.id ? { ...r, is_active: !item.is_active } : r) }));
        toast({ title: "Success", description: `Status changed to ${!item.is_active ? "Active" : "Inactive"}.` });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  // Backend POST /IcdNotCovered/addNewIcdNotCovered expects entity relations as
  // arrays of IDs/codes (strings), not display objects.
  const toIdString = (value: any): string => {
    if (value === null || value === undefined) return "";
    if (typeof value === "string" || typeof value === "number") return String(value);

    const raw =
      value?.value?.id ??
      value?.value ??
      value?.id ??
      value?.providerDbId ??
      value?.dbId ??
      value?.provider_id ??
      value?.Provider_id ??
      value?.Doctor_id;

    return raw === null || raw === undefined ? "" : String(raw);
  };

  const toIdStringArray = (values: any[]): string[] =>
    Array.from(
      new Set(
        (values || [])
          .map(toIdString)
          .map((value) => value.trim())
          .filter(Boolean)
      )
    );

  const toNumberArray = (values: any[]): number[] =>
    Array.from(
      new Set(
        (values || [])
          .map((value) => Number(toIdString(value)))
          .filter((value) => Number.isFinite(value))
      )
    );

  const validateRelationForm = (currentId?: string | number | null): string | null => {
    const trimmed = relationName.trim();
    if (!trimmed) {
      return "Relation Name is required to add this relation.";
    }

    // Normalized relation_name uniqueness (Small, capital letters, spaces... represent same relation as long as it's the same name)
    const normalizedNew = trimmed.toLowerCase().replace(/\s+/g, "");
    const duplicate = (items.data || []).find((item: any) => {
      if (currentId !== null && currentId !== undefined && (item.id === currentId || String(item.id) === String(currentId))) {
        return false;
      }
      if ((item as any).is_deleted) return false;
      const normExisting = String(item.relation_name || "").toLowerCase().replace(/\s+/g, "");
      return normExisting === normalizedNew;
    });
    if (duplicate) {
      return "Relation name already exists inside this rule.";
    }

    const hasIcd =
      medicalcode.length > 0 ||
      medicalIcdGroups.length > 0 ||
      customIcdGroups.length > 0;
    if (!hasIcd) {
      return "At least one active ICD code or group is required to add this relation.";
    }

    if (isActive === undefined || isActive === null) {
      return "Status is required to add this relation.";
    }

    return null;
  };

  const addItem = async (): Promise<void> => {
    const formError = validateRelationForm(null);
    if (formError) {
      toast({ title: "Validation Error", description: formError, variant: "destructive" });
      return;
    }
    if (relationName.trim().length < 3) { toast({ title: "Error", description: "Relation Name must be at least 3 characters long.", variant: "destructive" }); return; }
    const entityError = validateEntities();
    if (entityError) { toast({ title: "Validation Error", description: entityError, variant: "destructive" }); return; }

    const ValuesMedicalcode = medicalcode.map((o) => String(o.value));
    const ValuesCustomIcdGroups = toNumberArray(customIcdGroups as any[]);
    const ValuesMedicalIcdGroups = toNumberArray(medicalIcdGroups as any[]);
    const ValuesProvider = toIdStringArray(provider as any[]);
    const ValuesNetwork = toIdStringArray(networks as any[]);
    const ValuesPolicy = toIdStringArray(policy as any[]);
    const ValuesInsurancePlan = toIdStringArray(insuranceplan as any[]);
    const ValuesPatient = toIdStringArray(patients as any[]);
    const ValuesVip = toIdStringArray(vip as any[]);
    const ValuesDoctor = toIdStringArray(doctor as any[]);
    const ValuesSpecialty = toIdStringArray(specialty as any[]);

    try {
      const newGroupArray = [{
        relation_name: relationName,
        icd: ValuesMedicalcode,
        custom_groups: ValuesCustomIcdGroups,
        medical_groups: ValuesMedicalIcdGroups,
        provider: ValuesProvider,
        provider_mode: cardToMode(providerMode),
        provider_all: selectAllProviders,
        network: ValuesNetwork,
        network_mode: cardToMode(networkMode),
        network_all: selectAllNetworks,
        insuranceplan: ValuesInsurancePlan,
        insuranceplan_mode: cardToMode(insuranceMode),
        insuranceplan_all: selectAllInsurance,
        payer: Payer.map(String),
        payer_mode: cardToMode(payerMode),
        payer_all: selectAllPayer,
        patient: ValuesPatient,
        patient_mode: cardToMode(patientMode),
        patient_all: selectAllPatients,
        policy: ValuesPolicy,
        policy_mode: cardToMode(policyMode),
        policy_all: selectAllPolicy,
        user_level: userLevel.map(String),
        user_mode: cardToMode(userMode),
        user_all: selectAllUser,
        vip: ValuesVip,
        vip_mode: cardToMode(vipMode),
        vip_all: selectAllVip,
        doctor: ValuesDoctor,
        doctor_mode: cardToMode(doctorMode),
        doctor_all: selectAllDoctor,
        specialty: ValuesSpecialty,
        specialty_mode: cardToMode(specialtyMode),
        specialty_all: selectAllSpecialty,
        is_active: isActive,
      }];
      const response = await IcdNotCoveredAxiosInstance.post("addNewIcdNotCovered", { data: newGroupArray, token });
      if (response.data.success) {
        await fetchItems();
        setCurrentPage(0);
        clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "New ICD Not Covered Group Added Successfully." });
      } else {
        const errorMessage = response.data.errors?.[0]?.error || response.data.error || "Failed to add the group.";
        toast({ title: "Error", description: errorMessage, variant: "destructive" });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const updateItem = async (id: string | number): Promise<void> => {
    const formError = validateRelationForm(id);
    if (formError) {
      toast({ title: "Validation Error", description: formError, variant: "destructive" });
      return;
    }
    if (relationName.trim().length < 3) { toast({ title: "Error", description: "Relation Name must be at least 3 characters long.", variant: "destructive" }); return; }
    const entityError = validateEntities();
    if (entityError) { toast({ title: "Validation Error", description: entityError, variant: "destructive" }); return; }

    const ValuesMedicalcode = medicalcode.map((o) => String(o.value));
    const ValuesCustomIcdGroups = toNumberArray(customIcdGroups as any[]);
    const ValuesMedicalIcdGroups = toNumberArray(medicalIcdGroups as any[]);
    const ValuesProvider = toIdStringArray(provider as any[]);
    const ValuesNetwork = toIdStringArray(networks as any[]);
    const ValuesPolicy = toIdStringArray(policy as any[]);
    const ValuesInsurancePlan = toIdStringArray(insuranceplan as any[]);
    const ValuesPatient = toIdStringArray(patients as any[]);
    const ValuesVip = toIdStringArray(vip as any[]);
    const ValuesDoctor = toIdStringArray(doctor as any[]);
    const ValuesSpecialty = toIdStringArray(specialty as any[]);

    // Backend PUT /IcdNotCovered/updateIcdNotCovered
    // Send the full configurable entity scope using SQL database IDs.
    const updatedItem = {
      token,
      data: {
        id: Number(id),
        relation_name: relationName.trim(),
        icd: ValuesMedicalcode,
        medical_groups: ValuesMedicalIcdGroups,
        custom_groups: ValuesCustomIcdGroups,

        provider: selectAllProviders ? [] : ValuesProvider,
        provider_mode: cardToMode(providerMode),
        provider_all: selectAllProviders,

        network: selectAllNetworks ? [] : ValuesNetwork,
        network_mode: cardToMode(networkMode),
        network_all: selectAllNetworks,

        insuranceplan: selectAllInsurance ? [] : ValuesInsurancePlan,
        insuranceplan_mode: cardToMode(insuranceMode),
        insuranceplan_all: selectAllInsurance,

        payer: selectAllPayer ? [] : Payer.map(String),
        payer_mode: cardToMode(payerMode),
        payer_all: selectAllPayer,

        patient: selectAllPatients ? [] : ValuesPatient,
        patient_mode: cardToMode(patientMode),
        patient_all: selectAllPatients,

        policy: selectAllPolicy ? [] : ValuesPolicy,
        policy_mode: cardToMode(policyMode),
        policy_all: selectAllPolicy,

        user_level: selectAllUser ? [] : userLevel.map(String),
        user_mode: cardToMode(userMode),
        user_all: selectAllUser,

        vip: selectAllVip ? [] : ValuesVip,
        vip_mode: cardToMode(vipMode),
        vip_all: selectAllVip,

        doctor: selectAllDoctor ? [] : ValuesDoctor,
        doctor_mode: cardToMode(doctorMode),
        doctor_all: selectAllDoctor,

        specialty: selectAllSpecialty ? [] : ValuesSpecialty,
        specialty_mode: cardToMode(specialtyMode),
        specialty_all: selectAllSpecialty,

        is_active: isActive,
      },
    };
    try {
      const response = await IcdNotCoveredAxiosInstance.put(`updateIcdNotCovered`, updatedItem);
      if (response.data.success) {
        await fetchItems();
        clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "ICD Not Covered Group updated successfully." });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const handleEdit = (item: Datum) => {
    setRelationName(item.relation_name);
    setMedicalcode(item.icd.map((code) => ({ value: code, label: code })));

    const CustomIcdGroupsData = Array.isArray(item.custom_groups) ? item.custom_groups.map((obj) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    setCustomIcdGroups(CustomIcdGroupsData.map((obj) => ({ value: obj.id, label: obj.name, ...linkedStatusFields(obj) })));

    const MedicalIcdGroupsData = Array.isArray(item.medical_groups) ? item.medical_groups.map((obj) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    setMedicalIcdGroups(MedicalIcdGroupsData.map((obj) => ({ value: obj.id, label: obj.name, ...linkedStatusFields(obj) })));

    const networksData = Array.isArray(item.network) ? item.network.map((obj: any) => typeof obj === "string" ? { id: 0, Network_name: obj } : obj) : [];
    const mappedNetworks = networksData.map((obj: any) => { const name = obj?.Network_name || obj?.network_name || obj?.name || ""; return { value: obj?.id, label: obj?.id ? `(${obj.id}) ${name}` : name, ...linkedStatusFields(obj) }; });
    setNetworks(mappedNetworks);
    const nCard = modeToCard(item.network_mode);
    setNetworkMode(nCard.cardMode); setIsNetworkActive(nCard.isActive || mappedNetworks.length > 0);
    setSelectAllNetworks(item.network_all ?? false);

    const insuranceplanData = Array.isArray(item.insuranceplan) ? item.insuranceplan.map((obj: any) => typeof obj === "string" ? { id: 0, Plan_name: obj } : obj) : [];
    const mappedInsurance = insuranceplanData.map((obj: any) => { const name = obj?.Plan_name || obj?.plan_name || obj?.name || ""; return { value: obj?.id, label: obj?.id ? `(${obj.id}) ${name}` : name, ...linkedStatusFields(obj) }; });
    setInsuranceplan(mappedInsurance);
    const iCard = modeToCard(item.insuranceplan_mode);
    setInsuranceMode(iCard.cardMode); setIsInsuranceActive(iCard.isActive || mappedInsurance.length > 0);
    setSelectAllInsurance(item.insuranceplan_all ?? false);

    const vipData = Array.isArray(item.vip) ? item.vip.map((obj: any) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    const mappedVip = vipData.map((obj: any) => ({ value: obj?.id, label: obj?.id ? `(${obj.id}) ${obj?.name || ""}`.trim() : (obj?.name || ""), ...linkedStatusFields(obj) }));
    setVip(mappedVip);
    const vCard = modeToCard(item.vip_mode);
    setVipMode(vCard.cardMode); setIsVipActive(vCard.isActive || mappedVip.length > 0);
    setSelectAllVip(item.vip_all ?? false);

    const ProvidersData = Array.isArray(item.provider) ? item.provider.map((obj: any) => typeof obj === "string" ? { id: 0, Provider_id: "", Provider_name: obj } : obj) : [];
    const mappedProviders = ProvidersData.map((obj: any) => {
      const sqlId = obj?.id;
      const businessId = obj?.Provider_id ?? obj?.provider_id ?? "";
      const name = obj?.Provider_name || obj?.provider_name || obj?.name || "";
      return { value: sqlId, label: businessId ? `(${businessId}) ${name}`.trim() : name, providerDbId: sqlId, providerId: businessId, ...linkedStatusFields(obj) };
    });
    setProvider(mappedProviders);
    const pCard = modeToCard(item.provider_mode);
    setProviderMode(pCard.cardMode); setIsProviderActive(pCard.isActive || mappedProviders.length > 0);
    setSelectAllProviders(item.provider_all ?? false);

    const mappedPatients = (item.patient || []).map((p: any) => {
      if (typeof p === "string" || typeof p === "number") return { value: p, label: String(p) };
      const name = p?.full_name || p?.Full_name || p?.name || [p?.First_name ?? p?.first_name, p?.Last_name ?? p?.last_name].filter(Boolean).join(" ") || "Patient";
      const identifiers = [
        p?.National_id ?? p?.national_id ? `National ID: ${p?.National_id ?? p?.national_id}` : "",
        p?.Member_id ?? p?.member_id ? `Member ID: ${p?.Member_id ?? p?.member_id}` : "",
        p?.Patient_passport ?? p?.patient_passport ?? p?.passport ? `Passport: ${p?.Patient_passport ?? p?.patient_passport ?? p?.passport}` : "",
      ].filter(Boolean);
      return { value: p?.id, label: identifiers.length ? `${name} (${identifiers.join(" | ")})` : name, ...linkedStatusFields(p) };
    });
    setPatients(mappedPatients);
    const patCard = modeToCard(item.patient_mode);
    setPatientMode(patCard.cardMode); setIsPatientActive(patCard.isActive || mappedPatients.length > 0);
    setSelectAllPatients(item.patient_all ?? false);

    const mappedPolicy = Array.isArray(item.policy) ? item.policy.map((obj: any) => ({ id: obj.id || 0, Policy_id: obj.Policy_id || "", Policy_name: obj.Policy_name || "", plans: obj.plans || [], ...linkedStatusFields(obj) })) : [];
    setPolicy(mappedPolicy);
    const polCard = modeToCard(item.policy_mode);
    setPolicyMode(polCard.cardMode); setIsPolicyActive(polCard.isActive || mappedPolicy.length > 0);
    setSelectAllPolicy(item.policy_all ?? false);

    const DoctorsData = Array.isArray(item.doctor) ? item.doctor.map((obj: any) => typeof obj === "string" ? { id: 0, Doctor_id: "", Doctor_name: obj } : obj) : [];
    const mappedDoctors = DoctorsData.map((obj: any) => {
      const sqlId = obj?.id;
      const businessId = obj?.Doctor_id ?? obj?.doctor_id ?? "";
      const name = obj?.Doctor_name || obj?.doctor_name || obj?.name || "";
      return { value: sqlId, label: businessId ? `(${businessId}) ${name}`.trim() : name, dbId: sqlId, doctorId: businessId, ...linkedStatusFields(obj) };
    });
    setDoctor(mappedDoctors);
    const dCard = modeToCard(item.doctor_mode);
    setDoctorMode(dCard.cardMode); setIsDoctorActive(dCard.isActive || mappedDoctors.length > 0);
    setSelectAllDoctor(item.doctor_all ?? false);

    setPayer(item.payer || []);
    const payCard = modeToCard(item.payer_mode);
    setPayerMode(payCard.cardMode); setIsPayerActive(payCard.isActive || ((item.payer?.length ?? 0) > 0));
    setSelectAllPayer(item.payer_all ?? false);

    setUserLevel(item.user_level || []);
    const uCard = modeToCard(item.user_mode);
    setUserMode(uCard.cardMode); setIsUserActive(uCard.isActive || ((item.user_level?.length ?? 0) > 0));
    setSelectAllUser(item.user_all ?? false);

    const specialtyData = Array.isArray(item.specialty) ? item.specialty.map((obj: any) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    const mappedSpecialty = specialtyData.map((obj: any) => {
      const sqlId = obj?.id;
      const businessId = obj?.speciality_code ?? obj?.specialty_code ?? "";
      const name = obj?.name || obj?.Specialty_name || "";
      return { value: sqlId, label: businessId ? `(${businessId}) ${name}`.trim() : name, dbId: sqlId, speciality_code: businessId, ...linkedStatusFields(obj) };
    });
    setSpecialty(mappedSpecialty);
    const sCard = modeToCard(item.specialty_mode);
    setSpecialtyMode(sCard.cardMode);
    setIsSpecialtyActive(sCard.isActive || mappedSpecialty.length > 0);
    setSelectAllSpecialty(item.specialty_all ?? false);

    setIsActive(item.is_active);

    const hasEntities = !!(
      (item.provider?.length ?? 0) > 0 || (item.doctor?.length ?? 0) > 0 ||
      (item.network?.length ?? 0) > 0 || (item.insuranceplan?.length ?? 0) > 0 ||
      (item.policy?.length ?? 0) > 0 || (item.patient?.length ?? 0) > 0 ||
      (item.vip?.length ?? 0) > 0 || (item.payer?.length ?? 0) > 0 ||
      (item.user_level?.length ?? 0) > 0 ||
      (item.provider_mode      && item.provider_mode      !== "DISABLED") ||
      (item.network_mode       && item.network_mode       !== "DISABLED") ||
      (item.insuranceplan_mode && item.insuranceplan_mode !== "DISABLED") ||
      (item.payer_mode         && item.payer_mode         !== "DISABLED") ||
      (item.policy_mode        && item.policy_mode        !== "DISABLED") ||
      (item.patient_mode       && item.patient_mode       !== "DISABLED") ||
      (item.vip_mode           && item.vip_mode           !== "DISABLED") ||
      (item.doctor_mode        && item.doctor_mode        !== "DISABLED") ||
      (item.user_mode          && item.user_mode          !== "DISABLED") ||
      (item.specialty?.length ?? 0) > 0 ||
      (item.specialty_mode     && item.specialty_mode     !== "DISABLED")
    );
    setShowEntities(hasEntities);

    if (item.icd && item.icd.length > 0) setSelectedMainOption("list_of_icd");
    else if (item.medical_groups && item.medical_groups.length > 0) setSelectedMainOption("medical_icd_groups");
    else if (item.custom_groups && item.custom_groups.length > 0) setSelectedMainOption("custom_icd_groups");
    else setSelectedMainOption(null);

    setEditIndex(item.id);
  };

  const handleDownload = (item: Datum) => {
    const rows = ["relation_name;icd;medical_groups;custom_groups;provider;provider_mode;provider_all;networks;network_mode;network_all;insuranceplan;insuranceplan_mode;insuranceplan_all;payer;payer_mode;payer_all;patients;patient_mode;patient_all;policy;policy_mode;policy_all;user_level;user_mode;user_all;doctor;doctor_mode;doctor_all;vip;vip_mode;vip_all;is_active"];
    const icdCodes = item.icd || [];
    const medicalGroups = item.medical_groups || [];
    const customGroups = item.custom_groups || [];
    const providers = item.provider || [];
const nets = item.network || [];
const pats = item.patient || [];
    const ins = item.insuranceplan || [];
    const pol = item.policy || [];
    const ul = item.user_level || [];
    const doc = item.doctor || [];
    const pay = item.payer || [];
    const vips = item.vip || [];
    const maxLen = Math.max(icdCodes.length, medicalGroups.length, customGroups.length, providers.length, nets.length, ins.length, pats.length, pol.length, ul.length, doc.length, pay.length, vips.length);
    for (let index = 0; index < Math.max(1, maxLen); index++) {
      rows.push([
        item.relation_name || "",
        index < icdCodes.length ? icdCodes[index] : "",
        index < medicalGroups.length ? medicalGroups[index].name || "" : "",
        index < customGroups.length ? customGroups[index].name || "" : "",
        index < providers.length ? providers[index].Provider_name || "" : "",
        item.provider_mode ?? "DISABLED", item.provider_all ? "true" : "false",
        index < nets.length ? nets[index].network_name || "" : "",
        item.network_mode ?? "DISABLED", item.network_all ? "true" : "false",
        index < ins.length ? ins[index].Plan_name || "" : "",
        item.insuranceplan_mode ?? "DISABLED", item.insuranceplan_all ? "true" : "false",
        index < pay.length ? pay[index] : "",
        item.payer_mode ?? "DISABLED", item.payer_all ? "true" : "false",
        index < pats.length ? pats[index].name || "" : "",
        item.patient_mode ?? "DISABLED", item.patient_all ? "true" : "false",
        index < pol.length ? pol[index].Policy_name || "" : "",
        item.policy_mode ?? "DISABLED", item.policy_all ? "true" : "false",
        index < ul.length ? ul[index] : "",
        item.user_mode ?? "DISABLED", item.user_all ? "true" : "false",
        index < doc.length ? doc[index].Doctor_name || "" : "",
        item.doctor_mode ?? "DISABLED", item.doctor_all ? "true" : "false",
        index < vips.length ? vips[index].name || "" : "",
        item.vip_mode ?? "DISABLED", item.vip_all ? "true" : "false",
        item.is_active ? "TRUE" : "FALSE",
      ].join(";"));
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `ICDNotCovered${item.id}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const columns: ColumnDef<Datum>[] = [
    {
      accessorKey: "id",
      size: 80,
      header: ({ column }) => (
        <div className="w-full text-center">
          <Button {...qaAttrs("sort-id")} variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")} className="w-full">
            ID {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
          </Button>
        </div>
      ),
   cell: ({ row }) => (
  <div
    id={rowQaId("cell-id", row.original.id)}
    data-testid={rowQaId("cell-id", row.original.id)}
    data-qa={rowQaId("cell-id", row.original.id)}
    className="text-center w-full font-medium"
  >
    {currentPage * pageSize + row.index + 1}
  </div>
),
    },
    {
      accessorKey: "relation_name",
      size: 200,
      header: ({ column }) => (
        <div className="w-full text-center">
          <Button {...qaAttrs("sort-relation-name")} variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")} className="w-full">
            Name {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
          </Button>
        </div>
      ),
      cell: ({ row }) => {
        const codes = row.getValue("relation_name") as string[];
        const isDeleted = (row.original as any).is_deleted;
        return (
          <div
            id={rowQaId("cell-relation-name", row.original.id)}
            data-testid={rowQaId("cell-relation-name", row.original.id)}
            data-qa={rowQaId("cell-relation-name", row.original.id)}
            data-qa-deleted={isDeleted ? "true" : "false"}
            className={`text-center w-full ${isDeleted ? "line-through text-gray-400" : ""}`}
          >
            {Array.isArray(codes) ? codes.join(", ") : codes}
          </div>
        );
      },
    },
    {
      accessorKey: "icd",
      size: 250,
      header: ({ column }) => (
        <div className="w-full text-center">
          <Button {...qaAttrs("sort-icd")} variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")} className="w-full">
            ICD {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
          </Button>
        </div>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const icdCodes = Array.isArray(item.icd) ? item.icd : [];
        const icdCustom = Array.isArray(item.custom_groups)
          ? item.custom_groups.map((g: any) => `${g?.name || g}${linkedIsInactive(g) ? " (Inactive)" : ""}`)
          : [];
        const icdMedical = Array.isArray(item.medical_groups)
          ? item.medical_groups.map((g: any) => `${g?.name || g}${linkedIsInactive(g) ? " (Inactive)" : ""}`)
          : [];

        const allDisplay = [
          ...icdCodes.map((c: string) => ({ label: c, kind: "icd" as const })),
          ...icdMedical.map((g: string) => ({ label: g, kind: "medical" as const })),
          ...icdCustom.map((g: string) => ({ label: g, kind: "custom" as const })),
        ];

        if (allDisplay.length === 0) {
          return (
            <div
              id={rowQaId("cell-icd-empty", item.id)}
              data-testid={rowQaId("cell-icd-empty", item.id)}
              data-qa={rowQaId("cell-icd-empty", item.id)}
              className="w-full text-center text-gray-400"
            >
              —
            </div>
          );
        }

        const searchTerm = filterSearch.toLowerCase().trim();
        const hasSearch = searchTerm.length >= 2;

        const matchingValues = hasSearch
          ? allDisplay.filter((value) =>
              value.label.toLowerCase().includes(searchTerm),
            )
          : [];

        const displayedValues =
          hasSearch && matchingValues.length > 0
            ? matchingValues.slice(0, 3)
            : allDisplay.slice(0, 3);

        const remainingCount =
          hasSearch && matchingValues.length > 0
            ? matchingValues.length - 3
            : allDisplay.length - 3;

        const getStyle = (
          kind: "icd" | "medical" | "custom",
          isMatching: boolean,
        ) => {
          if (isMatching) {
            return {
              bg: "#fef08a",
              color: "#713f12",
              border: "#facc15",
              tag: kind === "icd" ? "ICD" : kind === "medical" ? "MED" : "CUS",
            };
          }

          if (kind === "icd") {
            return {
              bg: "#011934",
              color: "#fff",
              border: "#011934",
              tag: "ICD",
            };
          }

          if (kind === "medical") {
            return {
              bg: "#f0fdf4",
              color: "#15803d",
              border: "#86efac",
              tag: "MED",
            };
          }

          return {
            bg: "#faf5ff",
            color: "#7e22ce",
            border: "#d8b4fe",
            tag: "CUS",
          };
        };

        return (
          <div
            id={rowQaId("cell-icd", item.id)}
            data-testid={rowQaId("cell-icd", item.id)}
            data-qa={rowQaId("cell-icd", item.id)}
            className="w-full px-2 py-1.5"
          >
            <div
              id={rowQaId("icd-chip-list", item.id)}
              data-testid={rowQaId("icd-chip-list", item.id)}
              data-qa={rowQaId("icd-chip-list", item.id)}
              className="flex flex-wrap justify-center gap-1.5 mb-1"
            >
              {displayedValues.map((value, index) => {
                const isMatching =
                  hasSearch &&
                  value.label.toLowerCase().includes(searchTerm);
                const style = value.label.endsWith(" (Inactive)") ? { bg: "#e5e7eb", color: "#64748b", border: "#9ca3af", tag: value.kind === "medical" ? "MED" : "CUS" } : getStyle(value.kind, isMatching);

                return (
                  <div
                    key={`${value.kind}-${value.label}-${index}`}
                    id={rowQaId(`icd-chip-${index}`, item.id)}
                    data-testid={rowQaId(`icd-chip-${index}`, item.id)}
                    data-qa={rowQaId(`icd-chip-${index}`, item.id)}
                    data-qa-label={value.label}
                    data-qa-kind={value.kind}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "2px 4px 2px 8px",
                      borderRadius: 6,
                      background: style.bg,
                      border: `1px solid ${style.border}`,
                    }}
                  >
                    <span
                      id={rowQaId(`icd-chip-label-${index}`, item.id)}
                      data-testid={rowQaId(`icd-chip-label-${index}`, item.id)}
                      data-qa={rowQaId(`icd-chip-label-${index}`, item.id)}
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: style.color,
                      }}
                    >
                      {value.label}
                    </span>
                    <span
                      id={rowQaId(`icd-chip-type-${index}`, item.id)}
                      data-testid={rowQaId(`icd-chip-type-${index}`, item.id)}
                      data-qa={rowQaId(`icd-chip-type-${index}`, item.id)}
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        padding: "1px 5px",
                        borderRadius: 4,
                        background: "rgba(255,255,255,0.2)",
                        color: style.color,
                        opacity: 0.85,
                        border: `1px solid ${style.border}`,
                      }}
                    >
                      {style.tag}
                    </span>
                  </div>
                );
              })}
            </div>

            <div
              id={rowQaId("icd-chip-footer", item.id)}
              data-testid={rowQaId("icd-chip-footer", item.id)}
              data-qa={rowQaId("icd-chip-footer", item.id)}
              className="flex items-center justify-between border-t border-gray-100 pt-1"
            >
              <span
                id={rowQaId("icd-total-count", item.id)}
                data-testid={rowQaId("icd-total-count", item.id)}
                data-qa={rowQaId("icd-total-count", item.id)}
                className="text-xs font-bold text-gray-500"
              >
                {hasSearch && matchingValues.length > 0
                  ? `${matchingValues.length}/${allDisplay.length}`
                  : allDisplay.length}
              </span>

              {remainingCount > 0 && (
                <button
                  id={rowQaId("btn-show-more-icd-codes", item.id)}
                  data-testid={rowQaId("btn-show-more-icd-codes", item.id)}
                  data-qa={rowQaId("btn-show-more-icd-codes", item.id)}
                  type="button"
                  onClick={() => {
                    setIcdCodesDialogList(
                      hasSearch && matchingValues.length > 0
                        ? matchingValues
                        : allDisplay,
                    );
                    setIcdCodesDialogTitle(
                      `${item.relation_name} – ICD Codes`,
                    );
                    setIcdCodesDialogOpen(true);
                  }}
                  className={`text-xs font-semibold ${
                    hasSearch
                      ? "text-yellow-600 hover:text-yellow-800"
                      : "text-blue-600 hover:text-blue-800"
                  }`}
                >
                  +{remainingCount} more →
                </button>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "is_active",
      size: 90,
      header: () => <div {...qaAttrs("column-status-header")} className="w-full text-center normal-case font-normal">Status</div>,
      cell: ({ row }) => {
        const item = row.original;
        const isDeleted = (item as any).is_deleted;
        return (
          <div className="w-full flex flex-col items-center justify-center gap-1">
            <button
              id={rowQaId("toggle-status", item.id)}
              data-testid={rowQaId("toggle-status", item.id)}
              data-qa={rowQaId("toggle-status", item.id)}
              aria-label={`Toggle status for ICD Not Covered ${item.id}`}
              data-qa-status={item.is_active ? "active" : "inactive"}
              data-qa-deleted={isDeleted ? "true" : "false"}
              type="button"
              onClick={() => !isDeleted && toggleActiveStatus(item)}
              disabled={!!isDeleted}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${isDeleted ? "opacity-40 cursor-not-allowed bg-gray-200" : item.is_active ? "bg-green-500" : "bg-gray-300"}`}
            >
              <span
                id={rowQaId("toggle-status-knob", item.id)}
                data-testid={rowQaId("toggle-status-knob", item.id)}
                data-qa={rowQaId("toggle-status-knob", item.id)}
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.is_active && !isDeleted ? "translate-x-6" : "translate-x-1"}`}
              />
            </button>
          </div>
        );
      },
    },
    {
      id: "view",
      size: 70,
      header: () => (
        <div className="w-full text-center">
          <Button {...qaAttrs("column-view-header")} variant="ghost" className="w-full">View</Button>
        </div>
      ),
      cell: ({ row }) => (
        <div className="w-full flex justify-center py-2">
          <button
            id={rowQaId("btn-view-details", row.original.id)}
            data-testid={rowQaId("btn-view-details", row.original.id)}
            data-qa={rowQaId("btn-view-details", row.original.id)}
            onClick={() => handleOpenDialog(row.original)}
            className="text-blue-600 hover:text-blue-800"
            title="View Details"
            aria-label={`View ICD Not Covered details ${row.original.id}`}
          >
            <i
              id={rowQaId("icon-view-details", row.original.id)}
              data-testid={rowQaId("icon-view-details", row.original.id)}
              data-qa={rowQaId("icon-view-details", row.original.id)}
              className="fa-solid fa-eye text-lg"
            ></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div {...qaAttrs("page")} className="h5-container mx-auto px-1 pt-16 pb-4">
      <style>{`
        table { table-layout: fixed !important; width: 100% !important; }
        table th:nth-child(1), table td:nth-child(1) { width: 80px !important; }
        table th:nth-child(2), table td:nth-child(2) { width: 200px !important; }
        table th:nth-child(3), table td:nth-child(3) { width: 250px !important; }
        table th:nth-child(4), table td:nth-child(4) { width: 90px !important; }
        table th:nth-child(5), table td:nth-child(5) { width: 70px !important; }
        table th:nth-child(6), table td:nth-child(6) { width: 140px !important; }
        table th:nth-child(7), table td:nth-child(7) { width: 80px !important; }
        table th:nth-child(8), table td:nth-child(8) { width: 150px !important; }
        table th, table td {
          text-align: center !important; vertical-align: middle !important;
          padding: 12px 8px !important; overflow: hidden;
          text-overflow: ellipsis; white-space: nowrap;
        }
        table th { background: #1e3a8a !important; font-weight: 600 !important; color: #ffffff !important; }
      `}</style>

      <div {...qaAttrs("page-header")} className="flex justify-between items-center">
        <h5 {...qaAttrs("page-title")} className="mb-6 flex items-center gap-2 font-bold text-2xl mt-4 text-blue-900">
          <span {...qaAttrs("page-title-icon-wrapper")} className="icon-container flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white">
            <i {...qaAttrs("page-title-icon")} className="fa-solid fa-times-circle"></i>
          </span>
          ICD Not Covered
        </h5>
      </div>

      {/* ══ VIEW DIALOG ══════════════════════════════════════════════════════ */}
      <Dialog open={Boolean(selectedItem)} onOpenChange={(open) => { if (!open) handleCloseDialog(); }}>
        <DialogContent
          {...qaAttrs("dialog-view-details")}
          className="max-w-[900px] max-h-[92vh] flex flex-col p-0 overflow-hidden bg-white border-none rounded-2xl"
          style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)" }}
        >
          {selectedItem && (
            <>
              <div {...qaAttrs("view-details-header")} style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "24px 28px 20px", flexShrink: 0, position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
                <div style={{ position: "absolute", bottom: -30, left: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.03)" }} />
                <div style={{ display: "flex", alignItems: "flex-start", gap: 16, position: "relative", zIndex: 1 }}>
                  <div
                    {...qaAttrs("view-details-main-icon-wrapper")}
                    style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}
                  >
                    <i {...qaAttrs("view-details-main-icon")} className="fa-solid fa-times-circle" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2 }}>
                      <h2 {...qaAttrs("view-details-relation-name")} style={{ fontSize: 20, fontWeight: 700, color: "#fff", margin: 0, letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {selectedItem.relation_name}
                      </h2>
                      <span {...qaAttrs("view-details-status-badge")} data-qa-status={selectedItem.is_active ? "active" : "inactive"} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: selectedItem.is_active ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.15)", color: selectedItem.is_active ? "#86efac" : "#fca5a5", border: `1px solid ${selectedItem.is_active ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.2)"}` }}>
                        <span {...qaAttrs("view-details-status-dot")} style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />
                        <span {...qaAttrs("view-details-status-label")}>{selectedItem.is_active ? "Active" : "Inactive"}</span>
                      </span>
                      {/* ✅ Deleted badge */}
                      {(selectedItem as any).is_deleted && (
                        <span {...qaAttrs("view-details-deleted-badge")} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: "rgba(239,68,68,0.15)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.2)" }}>
                          <i {...qaAttrs("view-details-deleted-icon")} className="fa-solid fa-trash" style={{ fontSize: 10 }} /> <span {...qaAttrs("view-details-deleted-label")}>Deleted</span>
                        </span>
                      )}
                    </div>
                    <p {...qaAttrs("view-details-rule-id")} style={{ fontSize: 12, color: "rgba(147,197,253,0.8)", margin: 0, fontWeight: 400 }}>
                      Rule ID: <span style={{ fontWeight: 600, color: "#93c5fd" }}>#{selectedItem.id}</span>
                    </p>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginTop: 18, position: "relative", zIndex: 1 }}>
                  {[
                    { icon: "fa-ban",         label: "ICD Codes",      value: `${(selectedItem.icd || []).length} codes`, accent: "#60a5fa" },
                    { icon: "fa-layer-group", label: "Medical Groups",  value: `${(selectedItem.medical_groups || []).length}`, accent: "#a78bfa" },
                    { icon: "fa-cubes",       label: "Custom Groups",   value: `${(selectedItem.custom_groups || []).length}`, accent: "#34d399" },
                  ].map((card, i) => (
                    <div
                      key={i}
                      id={qaId(`view-details-summary-card-${i}`)}
                      data-testid={qaId(`view-details-summary-card-${i}`)}
                      data-qa={qaId(`view-details-summary-card-${i}`)}
                      data-qa-label={card.label}
                      style={{ background: "rgba(255,255,255,0.07)", backdropFilter: "blur(6px)", borderRadius: 12, padding: "12px 14px", border: "1px solid rgba(255,255,255,0.08)" }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                        <i id={qaId(`view-details-summary-card-${i}-icon`)} data-testid={qaId(`view-details-summary-card-${i}-icon`)} data-qa={qaId(`view-details-summary-card-${i}-icon`)} className={`fa-solid ${card.icon}`} style={{ fontSize: 11, color: card.accent, opacity: 0.9 }} />
                        <span id={qaId(`view-details-summary-card-${i}-label`)} data-testid={qaId(`view-details-summary-card-${i}-label`)} data-qa={qaId(`view-details-summary-card-${i}-label`)} style={{ fontSize: 10, color: "rgba(147,197,253,0.7)", fontWeight: 500, letterSpacing: "0.03em" }}>{card.label}</span>
                      </div>
                      <span id={qaId(`view-details-summary-card-${i}-value`)} data-testid={qaId(`view-details-summary-card-${i}-value`)} data-qa={qaId(`view-details-summary-card-${i}-value`)} style={{ fontSize: 15, fontWeight: 700, color: card.accent }}>{card.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div {...qaAttrs("view-details-body")} style={{ overflowY: "auto", flex: 1, background: "#f8fafc", padding: "6px 0 16px" }}>
                <div style={{ padding: "20px 28px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
                      <i className="fa-solid fa-ban" style={{ fontSize: 12, color: "#3b82f6" }} />
                    </div>
                    <span {...qaAttrs("view-details-icd-information-title")} style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>ICD Information</span>
                  </div>
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", marginBottom: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                      <span style={{ width: 6, height: 6, borderRadius: 2, background: "#3b82f6", flexShrink: 0 }} />
                      <span {...qaAttrs("view-details-icd-codes-label")} style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.02em" }}>ICD Codes</span>
                      <span {...qaAttrs("view-details-icd-codes-count")} style={{ fontSize: 10, color: "#94a3b8", marginLeft: "auto", fontWeight: 500 }}>{(selectedItem.icd || []).length} items</span>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 4, marginTop: 4 }}>
                      {(selectedItem.icd || []).length > 0
                        ? (selectedItem.icd || []).map((d: any, i: number) => (
                            <span
                              key={i}
                              id={qaId(`view-details-icd-code-${i}`)}
                              data-testid={qaId(`view-details-icd-code-${i}`)}
                              data-qa={qaId(`view-details-icd-code-${i}`)}
                              data-qa-code={toDisplayString(d)}
                              style={{ display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 9999, fontSize: 11, fontWeight: 500, background: "#eff6ff", color: "#1d4ed8", border: "0.5px solid #bfdbfe" }}
                            >
                              {toDisplayString(d)}
                            </span>
                          ))
                        : <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>None</span>}
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    {[
                      { label: "Medical ICD Groups", color: "#22c55e", items: viewStatusLabels(selectedItem.medical_groups || [], (selectedItem.medical_groups || []).map((g: any) => g.name || g)), tagStyle: { background: "#f0fdf4", color: "#15803d", border: "0.5px solid #bbf7d0" } },
                      { label: "Custom ICD Groups",  color: "#a855f7", items: viewStatusLabels(selectedItem.custom_groups || [], (selectedItem.custom_groups || []).map((g: any) => g.name || g)), tagStyle: { background: "#faf5ff", color: "#7e22ce", border: "0.5px solid #e9d5ff" } },
                    ].map((section) => (
                      <div key={section.label} style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                          <span style={{ width: 6, height: 6, borderRadius: 2, background: section.color, flexShrink: 0 }} />
                          <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.02em" }}>{section.label}</span>
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 4, marginTop: 4 }}>
                          {section.items.length > 0
                            ? section.items.map((item: string, i: number) => (
                              <span
                                key={i}
                                id={qaId(`view-details-group-item-${qaSafe(section.label)}-${i}`)}
                                data-testid={qaId(`view-details-group-item-${qaSafe(section.label)}-${i}`)}
                                data-qa={qaId(`view-details-group-item-${qaSafe(section.label)}-${i}`)}
                                data-qa-label={item}
                                style={{ display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 9999, fontSize: 11, fontWeight: 500, ...(item.endsWith(" (Inactive)") ? { background: "#e5e7eb", color: "#64748b", border: "1px solid #9ca3af" } : section.tagStyle) }}
                              >
                                {item}
                              </span>
                            ))
                            : <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>None</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />

                <div style={{ padding: "20px 28px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #fde68a" }}>
                      <i className="fa-solid fa-bullseye" style={{ fontSize: 12, color: "#d97706" }} />
                    </div>
                    <span {...qaAttrs("view-details-target-scope-title")} style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Target Scope & Visibility</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                    {[
                      { label: "Providers",       icon: "fa-hospital",         color: "#3b82f6", items: viewStatusLabels((selectedItem.provider || []) as any[], (selectedItem.provider     || []).map((p: any) => { const businessId = p?.Provider_id ?? p?.provider_id ?? ""; const name = p?.Provider_name || p?.provider_name || p?.name || ""; return businessId ? `(${businessId}) ${name}` : name || String(p); })), mode: (selectedItem as any).provider_mode,      all: (selectedItem as any).provider_all },
                      { label: "Doctors",          icon: "fa-stethoscope",      color: "#06b6d4", items: viewStatusLabels((selectedItem.doctor || []) as any[], (selectedItem.doctor       || []).map((d: any) => { const businessId = d?.Doctor_id ?? d?.doctor_id ?? ""; const name = d?.Doctor_name || d?.doctor_name || d?.name || ""; return businessId ? `(${businessId}) ${name}` : name || String(d); })), mode: (selectedItem as any).doctor_mode,        all: (selectedItem as any).doctor_all },
                      { label: "Networks",         icon: "fa-network-wired",    color: "#8b5cf6", items: viewStatusLabels((selectedItem.network || []) as any[], (selectedItem.network || []).map((n: any) => { const name = n?.Network_name || n?.network_name || n?.name || ""; return n?.id ? `(${n.id}) ${name}` : name || String(n); })), mode: (selectedItem as any).network_mode,       all: (selectedItem as any).network_all },
                      { label: "Insurance Plans",  icon: "fa-shield-halved",    color: "#f59e0b", items: viewStatusLabels((selectedItem.insuranceplan || []) as any[], (selectedItem.insuranceplan|| []).map((i: any) => { const name = i?.Plan_name || i?.plan_name || i?.name || ""; return i?.id ? `(${i.id}) ${name}` : name || String(i); })), mode: (selectedItem as any).insuranceplan_mode, all: (selectedItem as any).insuranceplan_all },
                      { label: "Policies",         icon: "fa-file-lines",       color: "#10b981", items: viewStatusLabels((selectedItem.policy || []) as any[], (selectedItem.policy || []).map((p: any) => { const planNames = (Array.isArray(p?.plans) ? p.plans : []).map((pl: any) => pl?.Plan_name || pl?.plan_name || pl?.name || "").filter(Boolean).join(", "); const businessId = p?.Policy_id ?? p?.policy_id ?? ""; const name = p?.Policy_name || p?.policy_name || p?.name || ""; const base = businessId ? `(${businessId}) ${name}` : name; return `${base}${planNames ? ` (${planNames})` : ""}`; }).filter(Boolean)), mode: (selectedItem as any).policy_mode, all: (selectedItem as any).policy_all },
                      { label: "Patients",         icon: "fa-users",            color: "#ec4899", items: viewStatusLabels((selectedItem.patient || []) as any[], (selectedItem.patient || []).map((p: any) => { if (typeof p !== "object" || p === null) return String(p); const name = p?.full_name || p?.Full_name || p?.name || [p?.First_name ?? p?.first_name, p?.Last_name ?? p?.last_name].filter(Boolean).join(" ") || "Patient"; const identifiers = [p?.National_id ?? p?.national_id ? `National ID: ${p?.National_id ?? p?.national_id}` : "", p?.Member_id ?? p?.member_id ? `Member ID: ${p?.Member_id ?? p?.member_id}` : "", p?.Patient_passport ?? p?.patient_passport ?? p?.passport ? `Passport: ${p?.Patient_passport ?? p?.patient_passport ?? p?.passport}` : ""].filter(Boolean); return identifiers.length ? `${name} (${identifiers.join(" | ")})` : name; })), mode: (selectedItem as any).patient_mode, all: (selectedItem as any).patient_all },
                      { label: "VIP",              icon: "fa-star",             color: "#eab308", items: viewStatusLabels((selectedItem.vip || []) as any[], (selectedItem.vip     || []).map((v: any) => v?.id ? `(${v.id}) ${v?.name || ""}`.trim() : v?.name || String(v))),           mode: (selectedItem as any).vip_mode,     all: (selectedItem as any).vip_all },
                      { label: "Payer",            icon: "fa-building-columns", color: "#6366f1", items: (selectedItem.payer   || []).map((p: any) => toDisplayString(p)),            mode: (selectedItem as any).payer_mode,   all: (selectedItem as any).payer_all },
                      { label: "Specialty",        icon: "fa-user-md",          color: "#0ea5e9", items: viewStatusLabels((selectedItem.specialty || []) as any[], (selectedItem.specialty  || []).map((s: any) => { const businessId = s?.speciality_code ?? s?.specialty_code ?? ""; const name = s?.name || s?.Specialty_name || ""; return businessId ? `(${businessId}) ${name}` : name || String(s); })),        mode: (selectedItem as any).specialty_mode, all: (selectedItem as any).specialty_all },
                      { label: "User Levels",      icon: "fa-user-gear",        color: "#64748b", items: (selectedItem.user_level || []).map((u: any) => toDisplayString(u)),         mode: (selectedItem as any).user_mode,    all: (selectedItem as any).user_all },
                    ].map((e) => {
                      const isExclude  = e.mode === "EXCLUDED";
                      const isDisabled = !e.mode || e.mode === "DISABLED";
                      return (
                        <div
                          key={e.label}
                          id={qaId(`view-details-entity-card-${qaSafe(e.label)}`)}
                          data-testid={qaId(`view-details-entity-card-${qaSafe(e.label)}`)}
                          data-qa={qaId(`view-details-entity-card-${qaSafe(e.label)}`)}
                          data-qa-mode={String(e.mode ?? "DISABLED")}
                          data-qa-all={e.all ? "true" : "false"}
                          style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", display: "flex", flexDirection: "column" as const }}
                        >
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                              <div style={{ width: 24, height: 24, borderRadius: 6, background: `${e.color}12`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <i className={`fa-solid ${e.icon}`} style={{ fontSize: 10, color: e.color }} />
                              </div>
                              <span id={qaId(`view-details-entity-card-${qaSafe(e.label)}-label`)} data-testid={qaId(`view-details-entity-card-${qaSafe(e.label)}-label`)} data-qa={qaId(`view-details-entity-card-${qaSafe(e.label)}-label`)} style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>{e.label}</span>
                            </div>
                            {!isDisabled && (
                              <span
                                id={qaId(`view-details-entity-card-${qaSafe(e.label)}-mode`)}
                                data-testid={qaId(`view-details-entity-card-${qaSafe(e.label)}-mode`)}
                                data-qa={qaId(`view-details-entity-card-${qaSafe(e.label)}-mode`)}
                                style={{ fontSize: 10, padding: "2px 8px", borderRadius: 9999, fontWeight: 600, background: isExclude ? "#fef2f2" : "#f0fdf4", color: isExclude ? "#dc2626" : "#15803d", border: `1px solid ${isExclude ? "#fecaca" : "#bbf7d0"}` }}
                              >
                                {isExclude ? "Exclusion" : "Inclusion"}
                              </span>
                            )}
                          </div>
                          {e.all
                            ? <span style={{ fontSize: 11, color: "#475569", fontStyle: "italic" }}>All {e.label}</span>
                            : e.items.length > 0
                              ? (
                                <div id={qaId(`view-details-entity-card-${qaSafe(e.label)}-items`)} data-testid={qaId(`view-details-entity-card-${qaSafe(e.label)}-items`)} data-qa={qaId(`view-details-entity-card-${qaSafe(e.label)}-items`)} style={{ display: "flex", flexWrap: "wrap" as const, gap: 5 }}>
                                  {e.items.map((item: string, i: number) => (
                                    <span
                                      key={i}
                                      id={qaId(`view-details-entity-card-${qaSafe(e.label)}-item-${i}`)}
                                      data-testid={qaId(`view-details-entity-card-${qaSafe(e.label)}-item-${i}`)}
                                      data-qa={qaId(`view-details-entity-card-${qaSafe(e.label)}-item-${i}`)}
                                      data-qa-label={item}
                                      style={{ fontSize: 11, padding: "3px 10px", borderRadius: 9999, background: item.endsWith(" (Inactive)") ? "#e5e7eb" : "#f1f5f9", color: item.endsWith(" (Inactive)") ? "#64748b" : "#334155", border: item.endsWith(" (Inactive)") ? "1px solid #9ca3af" : "1px solid #e2e8f0", fontWeight: 500 }}
                                    >
                                      {item}
                                    </span>
                                  ))}
                                </div>
                              )
                              : <span style={{ color: "#cbd5e1", fontSize: 12, fontStyle: "italic" }}>No items</span>
                          }
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />

                <div style={{ padding: "20px 28px 8px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                      <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
                    </div>
                    <span {...qaAttrs("view-details-audit-trail-title")} style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit Trail</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", letterSpacing: "0.02em" }}>Created By</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                          {String((selectedItem as any).created_by ?? "?")[0].toUpperCase()}
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{resolveUserName((selectedItem as any).created_by)}</span>
                      </div>
                    </div>
                    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", letterSpacing: "0.02em" }}>Created On</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                        <i className="fa-regular fa-calendar" style={{ fontSize: 14, color: "#3b82f6" }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                          {(selectedItem as any).createdat ? formatDisplayDateTime((selectedItem as any).createdat) : "—"}
                        </span>
                      </div>
                    </div>
                    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", letterSpacing: "0.02em" }}>Last Updated</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                        <i className="fa-regular fa-clock" style={{ fontSize: 14, color: "#10b981" }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                          {(selectedItem as any).updatedat ? formatDisplayDateTime((selectedItem as any).updatedat) : "—"}
                        </span>
                      </div>
                    </div>
                  </div>
            {Array.isArray((selectedItem as any).updated_by) && (selectedItem as any).updated_by.length > 0 && (
  <div style={{ gridColumn: "1 / -1", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.02em" }}>Update History</span>
      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
        {(selectedItem as any).updated_by.length} update{(selectedItem as any).updated_by.length !== 1 ? "s" : ""}
      </span>
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {((selectedItem as any).updated_by as { id?: number; user_id?: number; at?: string; date?: string; [key: string]: any }[]).slice().reverse().slice(0, showAllUpdates ? undefined : 4).map((entry, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
              {String(resolveUserName(entry.id ?? entry.user_id) ?? "?")[0].toUpperCase()}
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(entry.id ?? entry.user_id)}</span>
            {i === 0 && (<span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>Latest</span>)}
          </div>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>{formatDate(entry.date || entry.at || (entry as any).updatedat || (entry as any).timestamp || (i === 0 ? (selectedItem as any).updatedat : undefined))}</span>
        </div>
      ))}
      {(selectedItem as any).updated_by.length > 4 && (
        <div style={{ textAlign: "center", padding: "6px 0" }}>
          <button
            {...qaAttrs("btn-toggle-update-history")}
            type="button"
            onClick={() => setShowAllUpdates(v => !v)}
            style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}
          >
            {showAllUpdates ? "Show less ↑" : `+${(selectedItem as any).updated_by.length - 4} more updates ↓`}
          </button>
        </div>
      )}
    </div>
  </div>
)}
                </div>
              </div>

              <div style={{ padding: "14px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
                <Button {...qaAttrs("btn-close-view-details")} onClick={handleCloseDialog} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 28px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}>Close Details</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ══ ADD / EDIT DIALOG ════════════════════════════════════════════════ */}
  {/* ══ ADD / EDIT DIALOG ════════════════════════════════════════════════ */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent {...qaAttrs("dialog-add-edit")} className="max-w-[900px] w-[92vw] h-[88vh] flex flex-col overflow-hidden p-0">
          <div {...qaAttrs("dialog-add-edit-header")} className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
            <DialogHeader>
              <DialogTitle {...qaAttrs("dialog-add-edit-title")} className="text-lg font-semibold text-gray-900">
                {editIndex === null ? "Add ICD Not Covered " : "Edit ICD Not Covered "}
              </DialogTitle>
              </DialogHeader>
          </div>

          <div {...qaAttrs("dialog-add-edit-body")} className="flex-1 overflow-y-auto px-6 py-5 space-y-0">
            {/* Section 1 */}
            <SectionCard num={1} title="Basic Information" subtitle="Name and status of the group" automationId={qaId("section-basic-information")}>
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <Label {...qaAttrs("label-relation-name")} htmlFor={qaId("input-relation-name")}>
                    Relation Name <span className="text-red-500 ml-0.5">*</span>
                  </Label>
                  <Input
                    {...qaAttrs("input-relation-name")}
                    value={relationName}
                    onChange={(e) => setRelationName(e.target.value)}
                    placeholder="Must be at least 3 characters"
                    className="mt-1"
                  />
                </div>
                <div className="w-48 flex-shrink-0">
                  <Label {...qaAttrs("label-is-active")}>Status</Label>
                  <div className="mt-1">
                    <StatusToggle label="Group Status" dotColor="#2563eb" value={isActive} onChange={setIsActive} automationId={qaId("status")} />
                  </div>
                </div>
              </div>
            </SectionCard>

            {/* Section 2 */}
            <SectionCard num={2} title="ICD configuration *" subtitle="Choose one ICD source for this rule" automationId={qaId("section-icd-selection")}>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[
                  { key: "list_of_icd", label: "List of ICD", icon: "fa-file-medical", color: "blue", count: medicalcode.length },
                  { key: "medical_icd_groups", label: "Medical Groups", icon: "fa-layer-group", color: "green", count: medicalIcdGroups.length },
                  { key: "custom_icd_groups", label: "Custom Groups", icon: "fa-cubes", color: "purple", count: customIcdGroups.length },
                ].map((opt) => {
                  const isSelected = selectedMainOption === opt.key;
                  const colorMap: Record<string, { border: string; bg: string; text: string; iconBg: string }> = {
                    blue:   { border: "border-blue-500",   bg: "bg-blue-50",   text: "text-blue-700",   iconBg: "bg-blue-500 text-white" },
                    green:  { border: "border-green-500",  bg: "bg-green-50",  text: "text-green-700",  iconBg: "bg-green-500 text-white" },
                    purple: { border: "border-purple-500", bg: "bg-purple-50", text: "text-purple-700", iconBg: "bg-purple-500 text-white" },
                  };
                  const c = colorMap[opt.color];
                  return (
                    <button
                      key={opt.key}
                      id={qaId(`select-icd-source-${opt.key}`)}
                      data-testid={qaId(`select-icd-source-${opt.key}`)}
                      data-qa={qaId(`select-icd-source-${opt.key}`)}
                      data-qa-selected={isSelected ? "true" : "false"}
                      data-qa-count={opt.count}
                      type="button"
                      onClick={() => setSelectedMainOption(isSelected ? null : opt.key)}
                      className={`relative flex flex-col items-center gap-2 rounded-xl border-2 px-3 py-4 transition-all ${isSelected ? `${c.border} ${c.bg} shadow-sm` : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"}`}
                    >
                      {opt.count > 0 && (
                        <span
                          id={qaId(`select-icd-source-${opt.key}-count`)}
                          data-testid={qaId(`select-icd-source-${opt.key}-count`)}
                          data-qa={qaId(`select-icd-source-${opt.key}-count`)}
                          className={`absolute top-2 right-2 text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center ${isSelected ? c.iconBg : "bg-gray-200 text-gray-500"}`}
                        >
                          {opt.count}
                        </span>
                      )}
                      <span
                        id={qaId(`select-icd-source-${opt.key}-icon-wrapper`)}
                        data-testid={qaId(`select-icd-source-${opt.key}-icon-wrapper`)}
                        data-qa={qaId(`select-icd-source-${opt.key}-icon-wrapper`)}
                        className={`w-9 h-9 rounded-lg flex items-center justify-center ${isSelected ? c.iconBg : "bg-gray-100 text-gray-400"}`}
                      >
                        <i id={qaId(`select-icd-source-${opt.key}-icon`)} data-testid={qaId(`select-icd-source-${opt.key}-icon`)} data-qa={qaId(`select-icd-source-${opt.key}-icon`)} className={`fa-solid ${opt.icon} text-sm`}></i>
                      </span>
                      <span id={qaId(`select-icd-source-${opt.key}-label`)} data-testid={qaId(`select-icd-source-${opt.key}-label`)} data-qa={qaId(`select-icd-source-${opt.key}-label`)} className={`text-xs font-semibold text-center ${isSelected ? c.text : "text-gray-500"}`}>{opt.label}</span>
                    </button>
                  );
                })}
              </div>

              {selectedMainOption && (
                <div {...qaAttrs("selected-icd-source-content")} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  {selectedMainOption === "list_of_icd" && (
                    <div {...qaAttrs("wrapper-list-of-icd")}>
                      <Listofmedicalcode qaIdPrefix={qaId("list-of-icd")} onChange={handlemedicalcodeChange} description={Description} defaultValue={medicalcode} clear={clearmedicalcode} />
                    </div>
                  )}
                  {selectedMainOption === "medical_icd_groups" && (
                    <div {...qaAttrs("wrapper-medical-icd-groups")}>
                      <ListofMedicalGroup qaIdPrefix={qaId("medical-icd-groups")} onChange={handleMedicalIcdGroupsChange} defaultValue={medicalIcdGroups} clear={clearMedicalIcdGroups} />
                    </div>
                  )}
                  {selectedMainOption === "custom_icd_groups" && (
                    <div {...qaAttrs("wrapper-custom-icd-groups")}>
                      <ListofCustomIcd qaIdPrefix={qaId("custom-icd-groups")} onChange={handleCustomIcdGroupsChange} defaultValue={customIcdGroups} clear={clearCustomIcdGroups} />
                    </div>
                  )}
                </div>
              )}
            </SectionCard>

            {/* Section 3 – Entities */}
            <div {...qaAttrs("section-entities-configuration")} className="border-2 border-blue-200 rounded-xl overflow-hidden mb-4 shadow-sm">
              <div className="flex items-center justify-between px-5 py-3 bg-blue-50 border-b border-blue-200">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">3</span>
                  <div>
                    <p {...qaAttrs("section-entities-configuration-title")} className="text-sm font-semibold text-blue-900">Entities Configuration</p>
                  </div>
                </div>
                <button
                  {...qaAttrs("toggle-entities-configuration")}
                  type="button"
                  aria-pressed={showEntities}
                  onClick={handleToggleEntities}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${showEntities ? "bg-green-500" : "bg-gray-300"}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showEntities ? "translate-x-6" : "translate-x-1"}`} />
                </button>
              </div>

              {showEntities && (
                <div className="p-5 bg-blue-50">
                  <div className="flex gap-3 p-3 bg-blue-100 border-l-4 border-blue-500 rounded-r-lg mb-4">
                    <svg className="w-4 h-4 text-blue-700 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                    <p className="text-xs text-blue-900 leading-relaxed">
                      <span className="font-semibold text-emerald-700">Inclusion</span> = apply only to selected items &nbsp;·&nbsp;
                      <span className="font-semibold text-red-700">Exclusion</span> = apply to everyone except selected items
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <EntityCard automationId={qaId("entity-card-providers")} label="Providers" icon="fa-hospital" color="blue" isActive={isProviderActive} mode={providerMode}
                      onExclusion={() => { if (providerMode === "EXCLUSION") { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode(null); setIsProviderActive(false); } else { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode("EXCLUSION"); setIsProviderActive(true); } }}
                      onInclusion={() => { if (providerMode === "INCLUSION") { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode(null); setIsProviderActive(false); } else { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode("INCLUSION"); setIsProviderActive(true); } }}
                      onClear={() => { setProvider([]); setProviderMode(null); setIsProviderActive(false); setClearProvider(true); setSelectAllProviders(false); }}>
                      <label {...qaAttrs("label-select-all-providers")} className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input {...qaAttrs("checkbox-select-all-providers")} type="checkbox" checked={selectAllProviders} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllProviders(c); if (c) { setProvider([]); setClearProvider(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-blue-600" />
                        <span {...qaAttrs("label-text-select-all-providers")} className="text-sm font-medium text-slate-600">Select All Providers</span>
                      </label>
                      {!selectAllProviders && <div {...qaAttrs("wrapper-provider-selector")} style={{ display: "contents" }}><ListOfProviderName qaIdPrefix={qaId("provider")} onChange={handleProviderChange} defaultValue={provider} clear={clearProvider} /></div>}
                    </EntityCard>

                    <EntityCard automationId={qaId("entity-card-doctor")} label="Doctor" icon="fa-stethoscope" color="cyan" isActive={isDoctorActive} mode={doctorMode}
                      onExclusion={() => { if (doctorMode === "EXCLUSION") { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode(null); setIsDoctorActive(false); } else { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode("EXCLUSION"); setIsDoctorActive(true); } }}
                      onInclusion={() => { if (doctorMode === "INCLUSION") { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode(null); setIsDoctorActive(false); } else { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode("INCLUSION"); setIsDoctorActive(true); } }}
                      onClear={() => { setDoctor([]); setDoctorMode(null); setIsDoctorActive(false); setClearDoctor(true); setSelectAllDoctor(false); }}>
                      <label {...qaAttrs("label-select-all-doctors")} className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input {...qaAttrs("checkbox-select-all-doctors")} type="checkbox" checked={selectAllDoctor} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllDoctor(c); if (c) { setDoctor([]); setClearDoctor(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-cyan-600" />
                        <span {...qaAttrs("label-text-select-all-doctors")} className="text-sm font-medium text-slate-600">Select All Doctors</span>
                      </label>
                      {!selectAllDoctor && <div {...qaAttrs("wrapper-doctor-selector")} style={{ display: "contents" }}><ListOfDoctorName qaIdPrefix={qaId("doctor")} onChange={handleDoctorChange} defaultValue={doctor} clear={clearDoctor} /></div>}
                    </EntityCard>

                    <EntityCard automationId={qaId("entity-card-specialty")} label="Specialty" icon="fa-user-md" color="teal" isActive={isSpecialtyActive} mode={specialtyMode}
                      onExclusion={() => { if (specialtyMode === "EXCLUSION") { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode(null); setIsSpecialtyActive(false); } else { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode("EXCLUSION"); setIsSpecialtyActive(true); } }}
                      onInclusion={() => { if (specialtyMode === "INCLUSION") { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode(null); setIsSpecialtyActive(false); } else { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode("INCLUSION"); setIsSpecialtyActive(true); } }}
                      onClear={() => { setSpecialty([]); setSpecialtyMode(null); setIsSpecialtyActive(false); setClearSpecialty(true); setSelectAllSpecialty(false); }}>
                      <label {...qaAttrs("label-select-all-specialties")} className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input {...qaAttrs("checkbox-select-all-specialties")} type="checkbox" checked={selectAllSpecialty} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllSpecialty(c); if (c) { setSpecialty([]); setClearSpecialty(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-teal-600" />
                        <span {...qaAttrs("label-text-select-all-specialties")} className="text-sm font-medium text-slate-600">Select All Specialties</span>
                      </label>
                      {!selectAllSpecialty && (
                        <div {...qaAttrs("wrapper-specialty-selector")} style={{ display: "contents" }}><ListOfSpecialty qaIdPrefix={qaId("specialty")} onChange={(opts: Option[]) => setSpecialty(opts)} defaultValue={specialty} clear={clearSpecialty} /></div>
                      )}
                    </EntityCard>

                    <EntityCard automationId={qaId("entity-card-networks")} label="Networks" icon="fa-network-wired" color="purple" isActive={isNetworkActive} mode={networkMode}
                      onExclusion={() => { if (networkMode === "EXCLUSION") { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode(null); setIsNetworkActive(false); } else { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode("EXCLUSION"); setIsNetworkActive(true); } }}
                      onInclusion={() => { if (networkMode === "INCLUSION") { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode(null); setIsNetworkActive(false); } else { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode("INCLUSION"); setIsNetworkActive(true); } }}
                      onClear={() => { setNetworks([]); setNetworkMode(null); setIsNetworkActive(false); setClearNetworks(true); setSelectAllNetworks(false); }}>
                      <label {...qaAttrs("label-select-all-networks")} className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input {...qaAttrs("checkbox-select-all-networks")} type="checkbox" checked={selectAllNetworks} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllNetworks(c); if (c) { setNetworks([]); setClearNetworks(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-purple-600" />
                        <span {...qaAttrs("label-text-select-all-networks")} className="text-sm font-medium text-slate-600">Select All Networks</span>
                      </label>
                      {!selectAllNetworks && <div {...qaAttrs("wrapper-network-selector")} style={{ display: "contents" }}><ListOfNewNetworkName qaIdPrefix={qaId("network")} onChange={handleNetworkChange} defaultValue={networks} clear={clearNetworks} /></div>}
                    </EntityCard>

                    <EntityCard automationId={qaId("entity-card-insurance-plan")} label="Insurance Plan" icon="fa-shield-halved" color="amber" isActive={isInsuranceActive} mode={insuranceMode}
                      onExclusion={() => { if (insuranceMode === "EXCLUSION") { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode(null); setIsInsuranceActive(false); } else { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode("EXCLUSION"); setIsInsuranceActive(true); } }}
                      onInclusion={() => { if (insuranceMode === "INCLUSION") { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode(null); setIsInsuranceActive(false); } else { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode("INCLUSION"); setIsInsuranceActive(true); } }}
                      onClear={() => { setInsuranceplan([]); setInsuranceMode(null); setIsInsuranceActive(false); setClearInsuranceplan(true); setSelectAllInsurance(false); }}>
                      <label {...qaAttrs("label-select-all-insurance-plans")} className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input {...qaAttrs("checkbox-select-all-insurance-plans")} type="checkbox" checked={selectAllInsurance} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllInsurance(c); if (c) { setInsuranceplan([]); setClearInsuranceplan(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-amber-600" />
                        <span {...qaAttrs("label-text-select-all-insurance-plans")} className="text-sm font-medium text-slate-600">Select All Insurance Plans</span>
                      </label>
                      {!selectAllInsurance && <div {...qaAttrs("wrapper-insurance-plan-selector")} style={{ display: "contents" }}><ListOfInsuranceplanName qaIdPrefix={qaId("insurance-plan")} onChange={handleInsuranceplanChange} defaultValue={insuranceplan} clear={clearInsuranceplan} /></div>}
                    </EntityCard>

                    <EntityCard automationId={qaId("entity-card-policy")} label="Policy" icon="fa-file-lines" color="emerald" isActive={isPolicyActive} mode={policyMode}
                      onExclusion={() => { if (policyMode === "EXCLUSION") { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode(null); setIsPolicyActive(false); } else { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode("EXCLUSION"); setIsPolicyActive(true); } }}
                      onInclusion={() => { if (policyMode === "INCLUSION") { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode(null); setIsPolicyActive(false); } else { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode("INCLUSION"); setIsPolicyActive(true); } }}
                      onClear={() => { setPolicy([]); setPolicyMode(null); setIsPolicyActive(false); setClearPolicy(true); setSelectAllPolicy(false); }}>
                      <label {...qaAttrs("label-select-all-policies")} className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input {...qaAttrs("checkbox-select-all-policies")} type="checkbox" checked={selectAllPolicy} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPolicy(c); if (c) { setPolicy([]); setClearPolicy(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-emerald-600" />
                        <span {...qaAttrs("label-text-select-all-policies")} className="text-sm font-medium text-slate-600">Select All Policies</span>
                      </label>
                      {!selectAllPolicy && <div {...qaAttrs("wrapper-policy-selector")} style={{ display: "contents" }}><PolicyNames qaIdPrefix={qaId("policy")} onChange={handlePoliciesChange} defaultValue={policy} clear={clearPolicy} /></div>}
                    </EntityCard>

                    <EntityCard automationId={qaId("entity-card-patients")} label="Patients" icon="fa-users" color="pink" isActive={isPatientActive} mode={patientMode}
                      onExclusion={() => { if (patientMode === "EXCLUSION") { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode(null); setIsPatientActive(false); } else { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode("EXCLUSION"); setIsPatientActive(true); } }}
                      onInclusion={() => { if (patientMode === "INCLUSION") { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode(null); setIsPatientActive(false); } else { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode("INCLUSION"); setIsPatientActive(true); } }}
                      onClear={() => { setPatients([]); setPatientMode(null); setIsPatientActive(false); setClearPatient(true); setSelectAllPatients(false); }}>
                      <label {...qaAttrs("label-select-all-patients")} className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input {...qaAttrs("checkbox-select-all-patients")} type="checkbox" checked={selectAllPatients} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPatients(c); if (c) { setPatients([]); setClearPatient(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-pink-600" />
                        <span {...qaAttrs("label-text-select-all-patients")} className="text-sm font-medium text-slate-600">Select All Patients</span>
                      </label>
                      {!selectAllPatients && <div {...qaAttrs("wrapper-patient-selector")} style={{ display: "contents" }}><VipPatientSelector qaIdPrefix={qaId("patient")} onChange={handlePatientChange} defaultValue={patients} clear={clearPatient} /></div>}
                    </EntityCard>

                    <EntityCard automationId={qaId("entity-card-vip")} label="VIP" icon="fa-star" color="yellow" isActive={isVipActive} mode={vipMode}
                      onExclusion={() => { if (vipMode === "EXCLUSION") { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode(null); setIsVipActive(false); } else { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode("EXCLUSION"); setIsVipActive(true); } }}
                      onInclusion={() => { if (vipMode === "INCLUSION") { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode(null); setIsVipActive(false); } else { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode("INCLUSION"); setIsVipActive(true); } }}
                      onClear={() => { setVip([]); setVipMode(null); setIsVipActive(false); setClearVip(true); setSelectAllVip(false); }}>
                      <label {...qaAttrs("label-select-all-vip")} className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input {...qaAttrs("checkbox-select-all-vip")} type="checkbox" checked={selectAllVip} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllVip(c); if (c) { setVip([]); setClearVip(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-yellow-600" />
                        <span {...qaAttrs("label-text-select-all-vip")} className="text-sm font-medium text-slate-600">Select All VIP</span>
                      </label>
                      {!selectAllVip && <div {...qaAttrs("wrapper-vip-selector")} style={{ display: "contents" }}><Vip qaIdPrefix={qaId("vip")} onChange={handleVipChange} defaultValue={vip} clear={clearVip} /></div>}
                    </EntityCard>

                    <EntityCard automationId={qaId("entity-card-payer")} label="Payer" icon="fa-building-columns" color="indigo" isActive={isPayerActive} mode={payerMode}
                      onExclusion={() => { if (payerMode === "EXCLUSION") { setPayer([]); setSelectAllPayer(false); setPayerMode(null); setIsPayerActive(false); } else { setPayer([]); setSelectAllPayer(false); setPayerMode("EXCLUSION"); setIsPayerActive(true); } }}
                      onInclusion={() => { if (payerMode === "INCLUSION") { setPayer([]); setSelectAllPayer(false); setPayerMode(null); setIsPayerActive(false); } else { setPayer([]); setSelectAllPayer(false); setPayerMode("INCLUSION"); setIsPayerActive(true); } }}
                      onClear={() => { setPayer([]); setPayerMode(null); setIsPayerActive(false); setSelectAllPayer(false); }}>
                      <label {...qaAttrs("label-select-all-payers")} className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input {...qaAttrs("checkbox-select-all-payers")} type="checkbox" checked={selectAllPayer} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPayer(c); if (c) setPayer([]); }); }} className="w-4 h-4 rounded border-slate-300 accent-indigo-600" />
                        <span {...qaAttrs("label-text-select-all-payers")} className="text-sm font-medium text-slate-600">Select All Payers</span>
                      </label>
                      {!selectAllPayer && <Input {...qaAttrs("input-payer")} value={Payer.join(",")} onChange={(e) => setPayer([e.target.value])} placeholder="Enter Payer" />}
                    </EntityCard>

                    <EntityCard automationId={qaId("entity-card-user-level")} label="User Level" icon="fa-user-gear" color="slate" isActive={isUserActive} mode={userMode}
                      onExclusion={() => { if (userMode === "EXCLUSION") { setUserLevel([]); setSelectAllUser(false); setUserMode(null); setIsUserActive(false); } else { setUserLevel([]); setSelectAllUser(false); setUserMode("EXCLUSION"); setIsUserActive(true); } }}
                      onInclusion={() => { if (userMode === "INCLUSION") { setUserLevel([]); setSelectAllUser(false); setUserMode(null); setIsUserActive(false); } else { setUserLevel([]); setSelectAllUser(false); setUserMode("INCLUSION"); setIsUserActive(true); } }}
                      onClear={() => { setUserLevel([]); setUserMode(null); setIsUserActive(false); setSelectAllUser(false); }}>
                      <label {...qaAttrs("label-select-all-user-levels")} className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input {...qaAttrs("checkbox-select-all-user-levels")} type="checkbox" checked={selectAllUser} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllUser(c); if (c) setUserLevel([]); }); }} className="w-4 h-4 rounded border-slate-300 accent-slate-600" />
                        <span {...qaAttrs("label-text-select-all-user-levels")} className="text-sm font-medium text-slate-600">Select All User Levels</span>
                      </label>
                      {!selectAllUser && <Input {...qaAttrs("input-user-level")} value={userLevel.join(",")} onChange={(e) => setUserLevel([e.target.value])} placeholder="Enter User Level" />}
                    </EntityCard>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div {...qaAttrs("dialog-add-edit-footer")} className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
            <Button {...qaAttrs("btn-cancel-add-edit-dialog")} data-qa-action="cancel-add-edit" variant="outline" onClick={() => { setEditIndex(null); clear(); setIsDialogOpen(false); }}>Cancel</Button>
            <Button
              id={qaId(editIndex === null ? "btn-add-relation-submit" : "btn-update-relation-submit")}
              data-testid={qaId("btn-save-relation")}
              data-qa={qaId(editIndex === null ? "btn-add-relation-submit" : "btn-update-relation-submit")}
              data-qa-action={editIndex === null ? "add-relation" : "update-relation"}
              onClick={() => { if (editIndex === null) addItem(); else updateItem(editIndex); }}
              className="bg-blue-700 hover:bg-blue-800 text-white"
            >
              {editIndex === null ? "Add Relation " : "Update Relation "}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ══ ICD Codes Detail Dialog ══════════════════════════════════════════ */}
      <Dialog open={icdCodesDialogOpen} onOpenChange={setIcdCodesDialogOpen}>
        <DialogContent {...qaAttrs("dialog-icd-codes-details")} className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle {...qaAttrs("dialog-icd-codes-details-title")} className="flex items-center gap-2">
              <i {...qaAttrs("dialog-icd-codes-details-title-icon")} className="fa-solid fa-stethoscope text-rose-500" />
              <span {...qaAttrs("dialog-icd-codes-details-title-text")}>{icdCodesDialogTitle}</span>
              <span {...qaAttrs("dialog-icd-codes-details-count")} className="ml-2 text-sm font-normal text-gray-400">({icdCodesDialogList.length} codes)</span>
            </DialogTitle>
          </DialogHeader>
          <div {...qaAttrs("dialog-icd-codes-details-body")} className="mt-3 overflow-y-auto max-h-[calc(80vh-120px)] space-y-4">
            {icdCodesDialogList.filter(x => x.kind === "icd").length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: "#011934", display: "inline-block" }} />
                  <span className="text-xs font-bold text-gray-600 tracking-wide">ICD Codes ({icdCodesDialogList.filter(x => x.kind === "icd").length})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {icdCodesDialogList.filter(x => x.kind === "icd").map((c, i) => (
                    <span key={i} id={qaId(`dialog-icd-codes-details-icd-item-${i}`)} data-testid={qaId(`dialog-icd-codes-details-icd-item-${i}`)} data-qa={qaId(`dialog-icd-codes-details-icd-item-${i}`)} data-qa-label={c.label} style={{ padding: "4px 12px", background: "#011934", color: "white", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>{c.label}</span>
                  ))}
                </div>
              </div>
            )}
            {icdCodesDialogList.filter(x => x.kind === "medical").length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: "#16a34a", display: "inline-block" }} />
                  <span className="text-xs font-bold text-green-700 tracking-wide">Medical ICD Groups ({icdCodesDialogList.filter(x => x.kind === "medical").length})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {icdCodesDialogList.filter(x => x.kind === "medical").map((c, i) => (
                    <span key={i} id={qaId(`dialog-icd-codes-details-medical-item-${i}`)} data-testid={qaId(`dialog-icd-codes-details-medical-item-${i}`)} data-qa={qaId(`dialog-icd-codes-details-medical-item-${i}`)} data-qa-label={c.label} style={{ padding: "4px 12px", background: c.label.endsWith(" (Inactive)") ? "#e5e7eb" : "#f0fdf4", color: c.label.endsWith(" (Inactive)") ? "#64748b" : "#15803d", borderRadius: 6, fontSize: 12, fontWeight: 600, border: c.label.endsWith(" (Inactive)") ? "1px solid #9ca3af" : "1px solid #86efac" }}>{c.label}</span>
                  ))}
                </div>
              </div>
            )}
            {icdCodesDialogList.filter(x => x.kind === "custom").length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: "#9333ea", display: "inline-block" }} />
                  <span className="text-xs font-bold text-purple-700 tracking-wide">Custom ICD Groups ({icdCodesDialogList.filter(x => x.kind === "custom").length})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {icdCodesDialogList.filter(x => x.kind === "custom").map((c, i) => (
                    <span key={i} id={qaId(`dialog-icd-codes-details-custom-item-${i}`)} data-testid={qaId(`dialog-icd-codes-details-custom-item-${i}`)} data-qa={qaId(`dialog-icd-codes-details-custom-item-${i}`)} data-qa-label={c.label} style={{ padding: "4px 12px", background: c.label.endsWith(" (Inactive)") ? "#e5e7eb" : "#faf5ff", color: c.label.endsWith(" (Inactive)") ? "#64748b" : "#7e22ce", borderRadius: 6, fontSize: 12, fontWeight: 600, border: c.label.endsWith(" (Inactive)") ? "1px solid #9ca3af" : "1px solid #d8b4fe" }}>{c.label}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end pt-3 border-t mt-3">
            <Button {...qaAttrs("btn-close-icd-codes-details")} onClick={() => setIcdCodesDialogOpen(false)} className="bg-blue-700 hover:bg-blue-800">Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ══ DATA TABLE ═══════════════════════════════════════════════════════ */}
      <div
        {...qaAttrs("data-table-wrapper")}
        data-delete-action-testid={qaId("data-table-action-delete")}
        data-delete-icon-testid={qaId("data-table-action-delete-icon")}
        data-restore-action-testid={qaId("data-table-action-restore")}
        data-hard-delete-action-testid={qaId("data-table-action-hard-delete")}
      >
      <DataTable2
        qaIdPrefix={qaId("data-table")}
        columns={columns}
        data={items.data}
        searchColumns={[]}
        addButtonLabel="Add New Relation"
        showAddButton={false}
        onEdit={handleEdit}
        pageIndex={currentPage}
        onPageChange={setCurrentPage}
        manualPagination={manualPagination}
        pageCount={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setCurrentPage(0);
        }}
        allowedPageSizes={allowedPageSizes}
        isLoading={isTableLoading}
        onDelete={(id) => {
          const item = items.data.find(i => i.id === id);
          if ((item as any)?.is_deleted) {
            setForceId(id);
            setForceAction("hardDelete");
            setForceError("");
            setForceDialogOpen(true);
          } else {
            removeItem(id);
          }
        }}
        onRestore={canManageDeleted ? restoreItem : undefined}
canHardDelete={canManageDeleted}
        onAdd={handleAddClick}
        onEditClick={handleEditClick}
        onDownload={handleDownload}
        isRowDeleted={(row: any) => row.is_deleted === true}
        toolbarExtras={
          <div {...qaAttrs("toolbar-filters")} style={{ width: "100%", position: "relative" }}>
<div {...qaAttrs("toolbar-row")} style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "10px 160px 10px 16px", background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)", borderRadius: 12, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", marginBottom: 12 }}>
              <button {...qaAttrs("btn-toolbar-add-new-relation")} type="button" onClick={handleAddClick} style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 18px", fontSize: 13, fontWeight: 600, color: "#ffffff", background: "#1e3a8a", border: "none", borderRadius: 8, cursor: "pointer", whiteSpace: "nowrap", boxShadow: "0 2px 6px rgba(30,58,138,0.25)" }}>
                <svg {...qaAttrs("btn-toolbar-add-new-relation-icon")} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
                <span {...qaAttrs("btn-toolbar-add-new-relation-label")}>Add New Relation</span>
              </button>
              <div {...qaAttrs("wrapper-toolbar-search")}>
                <UnifiedSearchBox
                  value={filterSearch}
                  onChange={(value) => { setFilterSearch(value); setCurrentPage(0); }}
                  helpText="Relation Name, ICD, Medical Group, or Custom Group."
                />
              </div>
              <div {...qaAttrs("toolbar-separator")} style={{ width: 1, height: 28, background: "#cbd5e1" }} />
              <div {...qaAttrs("status-filter-section")} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span {...qaAttrs("status-filter-label")} style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.05em" }}>Status</span>
                <div {...qaAttrs("status-filter-wrapper")} style={{ display: "flex", alignItems: "center", padding: 4, background: "#e2e8f0", borderRadius: 9999 }}>
                  {[{ value: "", label: "All" }, { value: "true", label: "Active" }, { value: "false", label: "Inactive" }].map((option) => (
                    <button
                      key={option.value}
                      id={qaId(`btn-filter-status-${option.value || "all"}`)}
                      data-testid={qaId(`btn-filter-status-${option.value || "all"}`)}
                      data-qa={qaId(`btn-filter-status-${option.value || "all"}`)}
                      data-qa-selected={filterStatus === option.value ? "true" : "false"}
                      type="button"
                      onClick={() => setFilterStatus(option.value)}
                      style={{ padding: "6px 14px", fontSize: 13, fontWeight: 500, borderRadius: 9999, border: "none", cursor: "pointer", background: filterStatus === option.value ? option.value === "true" ? "#10b981" : option.value === "false" ? "#94a3b8" : "#ffffff" : "transparent", color: filterStatus === option.value ? option.value === "" ? "#334155" : "#ffffff" : "#64748b", boxShadow: filterStatus === option.value ? "0 2px 4px rgba(0,0,0,0.1)" : "none" }}>
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              <div {...qaAttrs("deleted-filter-wrapper")}>
                <ServerDeletedPills
                  value={deletedFilter}
                  onChange={(value) => { setDeletedFilter(value); setCurrentPage(0); }}
                />
              </div>
            </div>
            <div {...qaAttrs("wrapper-file-upload")} style={{ position: "absolute", top: 0, right: 16, height: 62, display: "flex", alignItems: "center" }}>
              <FileUpload fetchItems={fetchItems} />
            </div>
          </div>
        }
      />
      </div>

      {/* ─── Force / Hard Delete Dialog ───────────────────────────────────────── */}
      {forceDialogOpen && (
        <div {...qaAttrs("dialog-force-delete-overlay")} style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
          <div
            {...qaAttrs("dialog-force-delete")}
            data-qa-force-action={forceAction ?? ""}
            data-qa-force-id={forceId ?? ""}
            style={{ background: "#fff", borderRadius: 16, padding: 32, width: 440, boxShadow: "0 20px 50px rgba(0,0,0,0.3)", border: "1px solid #fecaca" }}
          >
            <div {...qaAttrs("dialog-force-delete-content")} style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 12 }}>
              <div
                {...qaAttrs("delete-alert-icon-wrapper")}
                style={{ width: 60, height: 60, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <i
                  {...qaAttrs("delete-alert-icon")}
                  className="fa-solid fa-triangle-exclamation"
                  style={{ fontSize: 28, color: "#dc2626" }}
                ></i>
              </div>

              {/* Hard Delete */}
              {forceAction === "hardDelete" && (
                <>
                  <h3
                    {...qaAttrs("hard-delete-alert-title")}
                    style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}
                  >
                    Permanent Delete
                  </h3>
                  <p
                    {...qaAttrs("hard-delete-alert-message")}
                    style={{ fontSize: 13, color: "#64748b", margin: 0, lineHeight: 1.6 }}
                  >
                    This will <strong style={{ color: "#dc2626" }}>permanently delete</strong> the record.<br />This action <strong>cannot be undone</strong>.
                  </p>
                  {forceError && (
                    <div
                      {...qaAttrs("hard-delete-error-container")}
                      style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}
                    >
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <i
                          {...qaAttrs("hard-delete-error-icon")}
                          className="fa-solid fa-circle-info"
                          style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}
                        ></i>
                        <p
                          {...qaAttrs("hard-delete-error-message")}
                          style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}
                        >
                          {forceError}
                        </p>
                      </div>
                    </div>
                  )}
               <div {...qaAttrs("hard-delete-actions")} style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
  <button
    {...qaAttrs("btn-cancel-hard-delete")}
    data-qa-action="cancel-hard-delete"
    onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
    style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
    Cancel
  </button>
  <button
    {...qaAttrs("btn-confirm-hard-force-delete")}
    data-qa-action="confirm-hard-force-delete"
    onClick={() => forceId !== null && permanentDeleteItem(forceId, true)}
    style={{ flex: 1, background: "#7f1d1d", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
    <i {...qaAttrs("icon-confirm-hard-force-delete")} className="fa-solid fa-bolt mr-2"></i>Force Delete Anyway
  </button>
</div>
                </>
              )}

              {/* Soft Delete */}
              {forceAction === "softDelete" && (
                <>
                  <h3
                    {...qaAttrs("soft-delete-alert-title")}
                    style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}
                  >
                    Record Is In Use
                  </h3>
                  <div
                    {...qaAttrs("soft-delete-error-container")}
                    style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <i
                        {...qaAttrs("soft-delete-error-icon")}
                        className="fa-solid fa-circle-info"
                        style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}
                      ></i>
                      <p
                        {...qaAttrs("soft-delete-error-message")}
                        style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}
                      >
                        {forceError}
                      </p>
                    </div>
                  </div>
                  <p
                    {...qaAttrs("soft-delete-confirmation-message")}
                    style={{ fontSize: 13, color: "#64748b", margin: 0 }}
                  >
                    Do you want to <strong style={{ color: "#dc2626" }}>force delete</strong> anyway?
                  </p>
                  <div {...qaAttrs("soft-delete-actions")} style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button {...qaAttrs("btn-cancel-soft-delete")} data-qa-action="cancel-soft-delete" onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
                    <button {...qaAttrs("btn-confirm-soft-force-delete")} data-qa-action="confirm-soft-force-delete" onClick={() => { if (forceId) forceSoftDelete(forceId); }}
                      style={{ flex: 1, background: "#dc2626", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i {...qaAttrs("icon-confirm-soft-force-delete")} className="fa-solid fa-bolt mr-2"></i>Force Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div {...qaAttrs("toast-wrapper")} data-delete-toast-message-testid={qaId("toast-delete-message")}>
        <Toaster />
      </div>
    </div>
  );
};

export default ICDNotCovered;
